import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import PlacementBracket from '../components/PlacementBracket';
import TournamentBracket from '../components/TournamentBracket';
import { useLanguage } from '../contexts/LanguageContext';

function AdminTournamentBracket() {
  const { t } = useLanguage();

  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [tournament, setTournament] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [groupPhaseCompleted, setGroupPhaseCompleted] = useState(false);
  const [tournamentCompleted, setTournamentCompleted] = useState(false);
  const [mexicanoStandings, setMexicanoStandings] = useState([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [generatingNextRound, setGeneratingNextRound] = useState(false);
  const [mexicanoCompleted, setMexicanoCompleted] = useState(false);
  const [completionRankings, setCompletionRankings] = useState(null);
  const [activeTab, setActiveTab] = useState('groups'); // 'groups', 'ko', 'placement', 'rankings'
  const [schedule, setSchedule] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const socketRef = useRef(null);
  const [editingMatch, setEditingMatch] = useState(null); // { id, court, start_time }
  const [bracketModal, setBracketModal] = useState(null); // match object für Bracket-Modal
  const [bracketModalMeta, setBracketModalMeta] = useState({ court: '', start_time: '', note: '' });

  // Manuelle Setzliste für ko_placement draw_mode=manual
  const [seedSlots, setSeedSlots] = useState(null); // Array der Team-IDs pro Seed-Position (0-basiert)
  const [seedDragSource, setSeedDragSource] = useState(null); // { type: 'pool'|'slot', teamId?, slotIndex? }
  const [generatingBracket, setGeneratingBracket] = useState(false);

  const loadTournaments = useCallback(async () => {
    try {
      const response = await axios.get('/api/tournaments');

      // Sort tournaments by date (newest first)
      const sortedTournaments = [...response.data].sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(b.date) - new Date(a.date);
      });

      setTournaments(sortedTournaments);
    } catch (err) {
      console.error('Fehler beim Laden der Turniere:', err);
      setError('Fehler beim Laden der Turniere');
    }
  }, []);

  const loadTournamentData = useCallback(async (tournamentId) => {
    setLoading(true);
    try {
      const [tournamentRes, teamsRes, matchesRes] = await Promise.all([
        axios.get(`/api/tournaments/${tournamentId}`),
        axios.get(`/api/tournaments/${tournamentId}/teams`),
        axios.get(`/api/tournaments/${tournamentId}/matches`)
      ]);

      setTournament(tournamentRes.data);
      setTeams(teamsRes.data);
      setMatches(matchesRes.data);
      setGroupPhaseCompleted(tournamentRes.data.group_phase_completed === 1);
      setTournamentCompleted(tournamentRes.data.is_completed === 1);

      // Load saved rankings if tournament is completed
      if (tournamentRes.data.is_completed === 1 && tournamentRes.data.final_rankings) {
        setCompletionRankings(tournamentRes.data.final_rankings);
      }

      // Load Mexicano standings if it's a Mexicano tournament
      if (tournamentRes.data.tournament_type === 'mexicano') {
        try {
          const standingsRes = await axios.get(`/api/tournaments/${tournamentId}/mexicano/standings`);
          setMexicanoStandings(standingsRes.data.standings || []);
          setCurrentRound(standingsRes.data.current_round || 0);
        } catch (err) {
          console.error('Fehler beim Laden der Mexicano-Rangliste:', err);
        }
      }

      if (tournamentRes.data.tournament_type === 'americano') {
        try {
          const standingsRes = await axios.get(`/api/tournaments/${tournamentId}/americano/standings`);
          setMexicanoStandings(standingsRes.data.standings || []);
          setCurrentRound(standingsRes.data.current_round || 0);
        } catch (err) {
          console.error('Fehler beim Laden der Americano-Rangliste:', err);
        }
      }

      // Load schedule if time planning is enabled
      if (['groups', 'ko_placement'].includes(tournamentRes.data.tournament_type) && tournamentRes.data.start_time) {
        try {
          const scheduleRes = await axios.get(`/api/tournaments/${tournamentId}/schedule`);
          if (scheduleRes.data.schedule) {
            setSchedule(scheduleRes.data.schedule);
          }
        } catch (err) {
          console.error('Fehler beim Laden des Zeitplans:', err);
        }
      }

      setError('');
    } catch (err) {
      console.error('Fehler beim Laden der Turnier-Daten:', err);
      setError('Fehler beim Laden der Turnier-Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
    socketRef.current = io(apiUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current.on('connect', () => {
      console.log('WebSocket connected (AdminTournamentBracket)');
      setWsConnected(true);
    });

    socketRef.current.on('disconnect', () => {
      console.log('WebSocket disconnected (AdminTournamentBracket)');
      setWsConnected(false);
    });

    socketRef.current.on('match_updated', (data) => {
      console.log('Match updated via WebSocket (AdminTournamentBracket):', data);
      // Update local matches state with new match data
      setMatches((prevMatches) =>
        prevMatches.map((match) =>
          match.id === data.match_id
            ? { ...match, ...data }
            : match
        )
      );
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Join/leave tournament room when tournament changes
  useEffect(() => {
    if (!selectedTournamentId || !socketRef.current) return;

    socketRef.current.emit('join_tournament', selectedTournamentId);
    console.log(`Joined tournament room (AdminTournamentBracket): ${selectedTournamentId}`);

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_tournament', selectedTournamentId);
        console.log(`Left tournament room (AdminTournamentBracket): ${selectedTournamentId}`);
      }
    };
  }, [selectedTournamentId]);

  useEffect(() => {
    if (selectedTournamentId) {
      loadTournamentData(selectedTournamentId);
    }
  }, [selectedTournamentId, loadTournamentData]);

  // Seed-Slots initialisieren wenn manuelles Monrad-Turnier geladen wird
  useEffect(() => {
    if (
      tournament?.tournament_type === 'ko_placement' &&
      tournament?.draw_mode === 'manual' &&
      tournament?.draw_generated !== 1
    ) {
      setSeedSlots(new Array(parseInt(tournament.num_teams)).fill(null));
      setSeedDragSource(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament?.id, tournament?.draw_mode, tournament?.draw_generated]);

  // Scroll to first unplayed match when matches are loaded or tab changes
  useEffect(() => {
    if (matches.length > 0) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        if (activeTab === 'groups') {
          const firstUnplayedMatch = matches.find(m => m.status === 'offen');
          if (firstUnplayedMatch) {
            const matchElement = document.getElementById(`match-${firstUnplayedMatch.id}`);
            if (matchElement) {
              matchElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              matchElement.style.border = '3px solid #e8b73a';
              setTimeout(() => {
                matchElement.style.border = '';
              }, 2000);
            }
          }
        } else if (activeTab === 'schedule' && schedule.length > 0) {
          const firstUnfinishedMatch = matches.find(m => m.status !== 'beendet');
          if (firstUnfinishedMatch) {
            const scheduleElement = document.getElementById(`schedule-item-${firstUnfinishedMatch.id}`);
            if (scheduleElement) {
              scheduleElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              scheduleElement.style.border = '3px solid #e8b73a';
              setTimeout(() => {
                scheduleElement.style.border = '';
              }, 2000);
            }
          }
        }
      }, 300);
    }
  }, [matches, activeTab, schedule]);

  const handleToggleActive = async () => {
    if (!tournament) return;

    try {
      await axios.put(
        `/api/tournaments/${tournament.id}`,
        { is_active: tournament.is_active === 1 ? 0 : 1 }
      );

      setMessage(tournament.is_active === 1 ? 'Turnier deaktiviert' : 'Turnier aktiviert');
      loadTournamentData(tournament.id);
      loadTournaments();

      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Fehler beim Aktivieren/Deaktivieren:', err);
      setError('Fehler beim Aktivieren/Deaktivieren des Turniers');
    }
  };

  // Hilfsfunktion für Mexicano: Zeige nur Spielernamen, nicht Teamnamen
  const getDisplayName = (team) => {
    if (!team) return 'TBD';

    // Bei Mexicano/Americano: Jedes "Team" ist eigentlich ein einzelner Spieler
    if (tournament?.tournament_type === 'mexicano' || tournament?.tournament_type === 'americano') {
      // Zeige nur den ersten Spielernamen (bei Mexicano/Americano hat jedes Team genau einen Spieler)
      return team.players?.[0]?.name || team.name;
    }

    // Bei anderen Turnieren: Zeige alle Spieler mit " / " getrennt
    return team.players?.map(p => p.name).join(' / ') || team.name;
  };

  const scrollToNextOpenMatch = (updatedMatchId) => {
    // Warte kurz, bis die Daten geladen sind, dann scrolle zum nächsten offenen Match
    setTimeout(() => {
      // Finde alle Match-Container (haben ID im Format "match-123")
      const allMatchElements = Array.from(document.querySelectorAll('[id^="match-"]'))
        .filter(el => {
          // Filtere nur die Haupt-Match-Container, nicht die Input-Felder
          const idMatch = el.id.match(/^match-(\d+)$/);
          return idMatch !== null;
        });

      let foundUpdatedMatch = false;
      let nextOpenMatch = null;

      // Durchsuche alle Matches nach dem aktualisierten Match und dann nach dem nächsten offenen
      for (let i = 0; i < allMatchElements.length; i++) {
        const matchElement = allMatchElements[i];
        const matchIdFromElement = matchElement.id.replace('match-', '');

        // Wenn wir das gerade aktualisierte Match gefunden haben, merken wir uns das
        if (parseInt(matchIdFromElement) === parseInt(updatedMatchId)) {
          foundUpdatedMatch = true;
          continue; // Überspringe das gerade gespeicherte Match
        }

        // Wenn wir das aktualisierte Match gefunden haben, suche nach dem nächsten offenen Match
        if (foundUpdatedMatch) {
          // Prüfe ob dieses Match offen ist (kein "✓ Beendet" Badge vorhanden)
          const isCompleted = matchElement.textContent.includes('✓ Beendet');
          if (!isCompleted) {
            nextOpenMatch = matchElement;
            break;
          }
        }
      }

      // Wenn ein nächstes offenes Match gefunden wurde, scrolle dorthin
      if (nextOpenMatch) {
        nextOpenMatch.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Fokussiere das erste Input-Feld in diesem Match
        setTimeout(() => {
          const firstInput = nextOpenMatch.querySelector('input[type="number"]');
          if (firstInput) {
            firstInput.focus();
          }
        }, 500);
      }
    }, 500);
  };

  const handleUpdateMatch = async (matchId, scoreData) => {
    try {
      // scoreData kann entweder {team1_score, team2_score} (Best of 1)
      // oder {team1_set1, team2_set1, ...} (Best of 3) enthalten
      await axios.put(
        `/api/matches/${matchId}/result`,
        {
          ...scoreData,
          status: 'beendet'
        }
      );

      // Für K.O. Turniere: Rücke Gewinner automatisch vor
      if (tournament && tournament.tournament_type === 'ko') {
        try {
          await axios.post(
            `/api/tournaments/${tournament.id}/ko/advance-winners`,
            {}
          );
        } catch (advanceErr) {
          console.error('Fehler beim Vorrücken der Gewinner:', advanceErr);
          // Fehler ignorieren, da Hauptupdate erfolgreich war
        }
      }

      setMessage('Ergebnis gespeichert');
      loadTournamentData(selectedTournamentId);

      // Scrolle zum nächsten offenen Match
      scrollToNextOpenMatch(matchId);

      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError('Fehler beim Speichern des Ergebnisses');
    }
  };

  const calcEndTime = (startStr) => {
    if (!startStr || !tournament?.match_duration) return null;
    const [h, m] = startStr.split(':').map(Number);
    const total = h * 60 + m + tournament.match_duration;
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  const handleSaveMatchDetails = async () => {
    if (!editingMatch) return;
    try {
      await axios.put(
        `/api/matches/${editingMatch.id}/details`,
        { court: editingMatch.court, start_time: editingMatch.start_time, note: editingMatch.note || '' }
      );
      setMessage('Platz / Zeit gespeichert');
      setEditingMatch(null);
      loadTournamentData(selectedTournamentId);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError('Fehler beim Speichern');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleClearAllTournaments = async () => {
    if (!window.confirm('⚠️ Wirklich ALLE Turniere löschen? Diese Aktion kann nicht rückgängig gemacht werden!')) {
      return;
    }

    setError('');
    setMessage('');

    try {
      await axios.delete('/api/tournaments/clear-all');

      setMessage('✅ Alle Turniere erfolgreich gelöscht');
      setSelectedTournamentId('');
      setTournament(null);
      setTeams([]);
      setMatches([]);
      loadTournaments(); // Liste neu laden (sollte leer sein)

      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error('Fehler beim Löschen aller Turniere:', err);
      setError('Fehler beim Löschen aller Turniere');
    }
  };

  const handleGenerateNextMexicanoRound = async () => {
    if (!tournament) return;

    setGeneratingNextRound(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(
        `/api/tournaments/${tournament.id}/mexicano/next-round`,
        {}
      );

      setMessage(`✅ ${response.data.message}`);
      loadTournamentData(tournament.id);

      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error('Fehler beim Generieren der nächsten Runde:', err);
      const errorMsg = err.response?.data?.detail || 'Fehler beim Generieren der nächsten Runde';
      setError(errorMsg);
      setTimeout(() => setError(''), 5000);
    } finally {
      setGeneratingNextRound(false);
    }
  };

  const handleGenerateNextAmericanoRound = async () => {
    if (!tournament) return;

    setGeneratingNextRound(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(
        `/api/tournaments/${tournament.id}/americano/next-round`,
        {}
      );

      setMessage(`✅ ${response.data.message}`);
      loadTournamentData(tournament.id);

      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error('Fehler beim Generieren der nächsten Runde:', err);
      const errorMsg = err.response?.data?.detail || 'Fehler beim Generieren der nächsten Runde';
      setError(errorMsg);
      setTimeout(() => setError(''), 5000);
    } finally {
      setGeneratingNextRound(false);
    }
  };


   // Check if all group matches are completed
   const checkAllGroupMatchesCompleted = useCallback(() => {
     if (!tournament || tournament.tournament_type !== 'groups' || !teams.length || !matches.length) {
       return false;
     }
 
     // Check if all group matches have results
     const groupMatches = matches.filter(match =>
       match.round && match.round.startsWith('Gruppe ') && match.status !== 'beendet'
     );
 
     return groupMatches.length === 0;
   }, [tournament, teams, matches]);
 
   // Check if all KO matches are completed
   const checkAllKOMatchesCompleted = useCallback(() => {
     if (!tournament || !matches.length) {
       return false;
     }

     // Check if all KO matches have results (including Trostrunden)
     // For KO tournaments, check ALL matches (main tournament + consolation rounds)
     const incompleteMatches = matches.filter(match => match.status !== 'beendet');

     return incompleteMatches.length === 0;
   }, [tournament, matches]);
 
   // Calculate tournament rankings
   // eslint-disable-next-line no-unused-vars
   const calculateRankings = useCallback(() => {
     if (!tournament || !matches.length || !teams.length) {
       return [];
     }
 
     // Find the final match to determine winner
     const finalMatch = matches.find(m => m.round === 'Finale' && m.status === 'beendet');
     const place3Match = matches.find(m => m.round === 'Spiel um Platz 3' && m.status === 'beendet');
 
     const rankings = [];
 
     // 1st place - winner of final
     if (finalMatch) {
       const winnerId = finalMatch.team1_score > finalMatch.team2_score
         ? finalMatch.team1_id
         : finalMatch.team2_id;
       const winnerTeam = teams.find(t => t.id === winnerId);
       if (winnerTeam) {
         rankings.push({
           place: 1,
           team: winnerTeam,
           players: winnerTeam.players.map(p => p.name).join(' / ')
         });
       }
     }
 
     // 2nd place - loser of final
     if (finalMatch) {
       const loserId = finalMatch.team1_score > finalMatch.team2_score
         ? finalMatch.team2_id
         : finalMatch.team1_id;
       const loserTeam = teams.find(t => t.id === loserId);
       if (loserTeam) {
         rankings.push({
           place: 2,
           team: loserTeam,
           players: loserTeam.players.map(p => p.name).join(' / ')
         });
       }
     }
 
     // 3rd place - winner of Spiel um Platz 3
     if (place3Match) {
       const winnerId = place3Match.team1_score > place3Match.team2_score
         ? place3Match.team1_id
         : place3Match.team2_id;
       const winnerTeam = teams.find(t => t.id === winnerId);
       if (winnerTeam) {
         rankings.push({
           place: 3,
           team: winnerTeam,
           players: winnerTeam.players.map(p => p.name).join(' / ')
         });
       }
     }
 
     return rankings;
   }, [tournament, matches, teams]);

   // Complete group phase and generate KO rounds
   const handleCompleteGroupPhase = async () => {
     if (!window.confirm('Sind Sie sicher, dass Sie die Gruppenphase abschließen möchten? Diese Aktion generiert die KO-Runden.')) {
       return;
     }

     try {
       setLoading(true);
       setError('');

       // Call backend to generate KO rounds
       await axios.post(
         `/api/tournaments/${tournament.id}/complete-group-phase`,
         {}
       );

       setGroupPhaseCompleted(true);
       setMessage('Gruppenphase erfolgreich abgeschlossen! KO-Runden wurden generiert.');
       loadTournamentData(tournament.id);

       setTimeout(() => setMessage(''), 5000);
     } catch (err) {
       console.error('Fehler beim Abschließen der Gruppenphase:', err);
       setError('Fehler beim Abschließen der Gruppenphase');
     } finally {
       setLoading(false);
     }
   };

   // Complete tournament and lock it
   const handleCompleteTournament = async () => {
     if (!window.confirm('🏆 Sind Sie sicher, dass Sie das Turnier beenden möchten? Das Turnier wird gesperrt und kann nicht mehr bearbeitet werden!')) {
       return;
     }

     try {
       setLoading(true);
       setError('');

       // Call backend to complete tournament
       const response = await axios.post(
         `/api/tournaments/${tournament.id}/complete`,
         {}
       );

       // Store rankings from backend response
       if (response.data.rankings) {
         setCompletionRankings(response.data.rankings);
       }

       setTournamentCompleted(true);
       setMessage('🏆 Turnier erfolgreich beendet! Rangliste wird angezeigt.');
       loadTournamentData(tournament.id);

       setTimeout(() => setMessage(''), 5000);
     } catch (err) {
       console.error('Fehler beim Beenden des Turniers:', err);
       setError(err.response?.data?.detail || 'Fehler beim Beenden des Turniers');
     } finally {
       setLoading(false);
     }
   };

   // Function to render KO phase bracket
   const renderKOPHase = () => {
     // Check if group phase is completed
     const allGroupMatchesCompleted = checkAllGroupMatchesCompleted();

     // Show button to complete group phase if all matches are done but KO phase not started
     if (allGroupMatchesCompleted && !groupPhaseCompleted) {
       return (
         <div className="card" style={{
           padding: '20px',
           textAlign: 'center',
           marginTop: '30px',
           backgroundColor: 'rgba(34, 197, 94, 0.1)',
           border: '2px solid #22c55e'
         }}>
           <h3 style={{ color: '#166534', marginBottom: '15px' }}>🎉 Alle Gruppenspiele abgeschlossen!</h3>
           <p style={{ margin: '0 0 20px 0', color: '#166534' }}>
             Sie können jetzt die Gruppenphase abschließen und die KO-Runden generieren.
           </p>
           <button
             onClick={handleCompleteGroupPhase}
             style={{
               padding: '12px 24px',
               background: '#166534',
               color: '#0F172A',
               border: 'none',
               borderRadius: '6px',
               fontSize: '1rem',
               fontWeight: 'bold',
               cursor: 'pointer'
             }}
           >
             Gruppenphase abschließen & KO-Runden generieren
           </button>
         </div>
       );
     }

     // If group phase is completed, show KO bracket
     if (groupPhaseCompleted) {
       // Check if all KO matches are completed to show finish button
       const allKOMatchesCompleted = checkAllKOMatchesCompleted();

       return (
         <div style={{ marginTop: '40px' }}>
           <h2 style={{ color: '#e8b73a', marginBottom: '20px', fontSize: '1.5rem', textAlign: 'center' }}>
             🏆 KO-Phase Turnierbaum
           </h2>

           {/* Turnierbaum */}
           <div style={{ marginTop: '20px', overflowX: 'auto' }}>
             <TournamentBracket
               matches={matches.filter(m => !m.round?.includes('Gruppe'))}
               teams={teams}
               readOnly={tournamentCompleted}
               isDarkMode={false}
               scheduleMap={Object.fromEntries(schedule.map(s => [s.match_id, s]))}
               onMatchClick={(match) => {
                 if (!tournamentCompleted) {
                   const sched = schedule.find(s => s.match_id === match.id);
                   setBracketModal(match);
                   setBracketModalMeta({
                     court: match.court || sched?.court || '',
                     start_time: match.start_time || sched?.start_time || '',
                     note: match.note || '',
                   });
                 }
               }}
             />
           </div>

           {/* Show finish tournament button if all KO matches are completed */}
           {allKOMatchesCompleted && !tournamentCompleted && (
             <div style={{ marginTop: '30px', textAlign: 'center' }}>
               <button
                 onClick={handleCompleteTournament}
                 disabled={loading}
                 style={{
                   padding: '12px 24px',
                   background: loading ? '#6b7280' : '#22c55e',
                   color: '#0F172A',
                   border: 'none',
                   borderRadius: '6px',
                   fontSize: '1rem',
                   fontWeight: 'bold',
                   cursor: loading ? 'not-allowed' : 'pointer',
                   opacity: loading ? 0.6 : 1
                 }}
               >
                 {loading ? t('admin.finishing') : t('admin.finishTournament')}
               </button>
             </div>
           )}

         </div>
       );
     }

     // If group phase not completed yet, show info
     if (!allGroupMatchesCompleted) {
       return (
         <div className="card" style={{
           padding: '20px',
           textAlign: 'center',
           marginTop: '30px',
           backgroundColor: 'rgba(251, 146, 60, 0.1)',
           border: '1px solid #fb923c'
         }}>
           <p style={{ margin: 0, color: '#92400e' }}>
             🔒 Die KO-Phase wird angezeigt, sobald alle Gruppenspiele abgeschlossen sind.
           </p>
         </div>
       );
     }

     return null;
   };

  // Helper function to format date with leading zeros (DD.MM.YYYY)
  const formatDateWithLeadingZeros = (date) => {
    if (!date) return t('admin.noDate');

    // Wenn Datum bereits im deutschen Format ist (DD.MM.YYYY), direkt zurückgeben
    if (typeof date === 'string' && date.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      return date;
    }

    // Ansonsten von ISO-Format konvertieren
    const d = new Date(date);
    if (isNaN(d.getTime())) return t('admin.invalidDate');

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const getTournamentLabel = (tournament) => {
    if (!tournament) return '';

    // Format date as DD.MM.YYYY with leading zeros
    const dateStr = formatDateWithLeadingZeros(tournament.date);

    // Get creator name
    const creatorName = [tournament.created_by_first_name, tournament.created_by_last_name]
      .filter(Boolean)
      .join(' ') || 'Unbekannt';

    // Build display string
    let display = `${dateStr} - ${tournament.name} - ${creatorName}`;

    // Add green checkmark for active tournaments
    if (tournament.is_active === 1) {
      display += ' ✓';
    }

    return display;
  };

  // Helper function to get descriptive placeholder for TBD teams
  const getTeamPlaceholder = (match, teamPosition) => {
    if (!match) return 'TBD';

    // Find which matches feed into this match (winners)
    const winnerFeedingMatches = matches.filter(m => m.next_match_id === match.id);
    // Find which matches feed into this match (losers for Spiel um Platz 3)
    const loserFeedingMatches = matches.filter(m => m.loser_next_match_id === match.id);

    // Determine placeholder based on round
    if (match.round === 'Halbfinale') {
      // Halbfinale: team1 = Sieger VF 1/3, team2 = Sieger VF 2/4
      const qfMatches = winnerFeedingMatches.filter(m => m.round === 'Viertelfinale')
        .sort((a, b) => a.match_number - b.match_number);

      if (qfMatches.length >= 2) {
        if (teamPosition === 'team1') {
          return `Sieger Viertelfinale ${qfMatches[0].match_number}`;
        } else {
          return `Sieger Viertelfinale ${qfMatches[1].match_number}`;
        }
      } else if (qfMatches.length === 1) {
        // Fallback wenn nur 1 Match gefunden wurde
        return `Sieger Viertelfinale ${qfMatches[0].match_number}`;
      }
    }
    else if (match.round === 'Finale') {
      // Finale: team1 = Sieger HF 1, team2 = Sieger HF 2
      const sfMatches = winnerFeedingMatches.filter(m => m.round === 'Halbfinale')
        .sort((a, b) => a.match_number - b.match_number);

      if (sfMatches.length >= 2) {
        if (teamPosition === 'team1') {
          return `Sieger Halbfinale ${sfMatches[0].match_number}`;
        } else {
          return `Sieger Halbfinale ${sfMatches[1].match_number}`;
        }
      } else if (sfMatches.length === 1) {
        // Fallback wenn nur 1 Match gefunden wurde
        return `Sieger Halbfinale ${sfMatches[0].match_number}`;
      }
    }
    else if (match.round === 'Spiel um Platz 3') {
      // Spiel um Platz 3: team1 = Verlierer HF 1, team2 = Verlierer HF 2
      const sfMatches = loserFeedingMatches.filter(m => m.round === 'Halbfinale')
        .sort((a, b) => a.match_number - b.match_number);

      if (sfMatches.length >= 2) {
        if (teamPosition === 'team1') {
          return `Verlierer Halbfinale ${sfMatches[0].match_number}`;
        } else {
          return `Verlierer Halbfinale ${sfMatches[1].match_number}`;
        }
      } else if (sfMatches.length === 1) {
        // Fallback wenn nur 1 Match gefunden wurde
        return `Verlierer Halbfinale ${sfMatches[0].match_number}`;
      }
    }

    return 'TBD';
  };

  // Standardized match card layout for KO phase
  const renderStandardMatchCard = (match) => {
    const team1 = teams.find(t => t.id === match.team1_id);
    const team2 = teams.find(t => t.id === match.team2_id);
    const matchSchedule = schedule.find(s => s.match_id === match.id);

    return (
      <div id={`match-${match.id}`} key={match.id} className="card" style={{
        padding: '20px',
        marginBottom: '15px',
        border: match.status === 'beendet' ? '2px solid #22c55e' : '1px solid rgba(206, 212, 218, 0.5)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        minWidth: '400px'
      }}>
        {/* Platz / Uhrzeit + Edit-Button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ fontSize: '0.8rem', color: '#6c757d', fontWeight: '500' }}>
            {(match.court || matchSchedule?.court) && (
              <span>🏟️ {match.court || matchSchedule?.court}</span>
            )}
            {(match.start_time || matchSchedule) && (
              <span style={{ marginLeft: (match.court || matchSchedule?.court) ? '10px' : 0, color: '#e8b73a' }}>
                ⏰ {match.start_time || matchSchedule?.start_time}
                {match.start_time
                  ? (calcEndTime(match.start_time) ? ` - ${calcEndTime(match.start_time)}` : '')
                  : (matchSchedule?.end_time ? ` - ${matchSchedule.end_time}` : '')}
              </span>
            )}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setEditingMatch({ id: match.id, court: match.court || '', start_time: match.start_time || '', note: match.note || '' }); }}
            title={t('admin.editCourtTime')}
            style={{
              background: 'none', border: '1px solid rgba(206,212,218,0.5)', borderRadius: '4px',
              cursor: 'pointer', fontSize: '0.85rem', padding: '2px 7px', color: '#6c757d'
            }}
          >✏️</button>
        </div>
        {/* Team Namen nebeneinander */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          gap: '15px',
          marginBottom: '15px',
          alignItems: 'center'
        }}>
          <div style={{
            fontWeight: 'bold',
            color: '#0F172A',
            fontSize: '15px',
            textAlign: 'left'
          }}>
            {team1 ? getDisplayName(team1) : getTeamPlaceholder(match, 'team1')}
          </div>
          <div style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#6c757d',
            padding: '0 10px'
          }}>vs</div>
          <div style={{
            fontWeight: 'bold',
            color: '#0F172A',
            fontSize: '15px',
            textAlign: 'right'
          }}>
            {team2 ? getDisplayName(team2) : getTeamPlaceholder(match, 'team2')}
          </div>
        </div>

        {/* Best of 3 oder Best of 1 Eingabe */}
        {tournament?.best_of_three === 1 ? (
          /* Best of 3 Eingabe - Spalten Layout */
          <div style={{ marginBottom: '15px' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '15px'
            }}>
              {/* Linke Spalte - Team 1 */}
              <div style={{
                padding: '12px',
                background: 'rgba(245, 242, 239, 0.5)',
                borderRadius: '6px'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginBottom: '8px',
                  fontWeight: '600'
                }}>
                  {team1 ? team1.players.map(p => p.name.split(' ')[0]).join('/') : 'Team 1'}
                </div>

                {/* Satz 1 */}
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                    {t('admin.set1')}:
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="7"
                    key={`${match.id}-team1-set1-${match.team1_set1}`}
                    defaultValue={match.team1_set1 !== null ? match.team1_set1 : ''}
                    placeholder="0"
                    id={`match-${match.id}-team1-set1`}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '16px',
                      textAlign: 'center',
                      background: 'white',
                      border: '2px solid rgba(206, 212, 218, 0.5)',
                      color: '#0F172A',
                      borderRadius: '4px',
                      fontWeight: 'bold'
                    }}
                  />
                </div>

                {/* Satz 2 */}
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                    {t('admin.set2')}:
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="7"
                    key={`${match.id}-team1-set2-${match.team1_set2}`}
                    defaultValue={match.team1_set2 !== null ? match.team1_set2 : ''}
                    placeholder="0"
                    id={`match-${match.id}-team1-set2`}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '16px',
                      textAlign: 'center',
                      background: 'white',
                      border: '2px solid rgba(206, 212, 218, 0.5)',
                      color: '#0F172A',
                      borderRadius: '4px',
                      fontWeight: 'bold'
                    }}
                  />
                </div>

                {/* Tiebreak */}
                <div>
                  <label style={{ fontSize: '12px', color: '#e8b73a', display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                    {t('admin.tiebreak')}:
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    key={`${match.id}-team1-set3-${match.team1_set3}`}
                    defaultValue={match.team1_set3 !== null && match.team1_set3 !== 0 ? match.team1_set3 : ''}
                    placeholder="0"
                    id={`match-${match.id}-team1-set3`}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '16px',
                      textAlign: 'center',
                      background: 'rgba(254, 243, 199, 0.3)',
                      border: '2px solid #e8b73a',
                      color: '#0F172A',
                      borderRadius: '4px',
                      fontWeight: 'bold'
                    }}
                  />
                </div>
              </div>

              {/* Rechte Spalte - Team 2 */}
              <div style={{
                padding: '12px',
                background: 'rgba(245, 242, 239, 0.5)',
                borderRadius: '6px'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginBottom: '8px',
                  fontWeight: '600'
                }}>
                  {team2 ? team2.players.map(p => p.name.split(' ')[0]).join('/') : 'Team 2'}
                </div>

                {/* Satz 1 */}
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                    {t('admin.set1')}:
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="7"
                    key={`${match.id}-team2-set1-${match.team2_set1}`}
                    defaultValue={match.team2_set1 !== null ? match.team2_set1 : ''}
                    placeholder="0"
                    id={`match-${match.id}-team2-set1`}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '16px',
                      textAlign: 'center',
                      background: 'white',
                      border: '2px solid rgba(206, 212, 218, 0.5)',
                      color: '#0F172A',
                      borderRadius: '4px',
                      fontWeight: 'bold'
                    }}
                  />
                </div>

                {/* Satz 2 */}
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                    {t('admin.set2')}:
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="7"
                    key={`${match.id}-team2-set2-${match.team2_set2}`}
                    defaultValue={match.team2_set2 !== null ? match.team2_set2 : ''}
                    placeholder="0"
                    id={`match-${match.id}-team2-set2`}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '16px',
                      textAlign: 'center',
                      background: 'white',
                      border: '2px solid rgba(206, 212, 218, 0.5)',
                      color: '#0F172A',
                      borderRadius: '4px',
                      fontWeight: 'bold'
                    }}
                  />
                </div>

                {/* Tiebreak */}
                <div>
                  <label style={{ fontSize: '12px', color: '#e8b73a', display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                    {t('admin.tiebreak')}:
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    key={`${match.id}-team2-set3-${match.team2_set3}`}
                    defaultValue={match.team2_set3 !== null && match.team2_set3 !== 0 ? match.team2_set3 : ''}
                    placeholder="0"
                    id={`match-${match.id}-team2-set3`}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '16px',
                      textAlign: 'center',
                      background: 'rgba(254, 243, 199, 0.3)',
                      border: '2px solid #e8b73a',
                      color: '#0F172A',
                      borderRadius: '4px',
                      fontWeight: 'bold'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Hinweis */}
            <div style={{
              fontSize: '11px',
              color: '#94a3b8',
              marginTop: '8px',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              {t('admin.tiebreakHint')}
            </div>
          </div>
        ) : (
          /* Best of 1 Eingabe - Spalten Layout */
          <div style={{ marginBottom: '15px' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '15px'
            }}>
              {/* Team 1 Score */}
              <div style={{
                padding: '12px',
                background: 'rgba(245, 242, 239, 0.5)',
                borderRadius: '6px'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginBottom: '8px',
                  fontWeight: '600'
                }}>
                  {team1 ? team1.players.map(p => p.name.split(' ')[0]).join('/') : 'Team 1'}
                </div>
                <input
                  type="number"
                  min="0"
                  key={`${match.id}-team1-score-${match.team1_score}`}
                  defaultValue={match.team1_score !== null ? match.team1_score : ''}
                  placeholder="0"
                  id={`match-${match.id}-team1`}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '20px',
                    textAlign: 'center',
                    background: 'white',
                    border: '2px solid rgba(206, 212, 218, 0.6)',
                    color: '#0F172A',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}
                />
              </div>

              {/* Team 2 Score */}
              <div style={{
                padding: '12px',
                background: 'rgba(245, 242, 239, 0.5)',
                borderRadius: '6px'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginBottom: '8px',
                  fontWeight: '600'
                }}>
                  {team2 ? team2.players.map(p => p.name.split(' ')[0]).join('/') : 'Team 2'}
                </div>
                <input
                  type="number"
                  min="0"
                  key={`${match.id}-team2-score-${match.team2_score}`}
                  defaultValue={match.team2_score !== null ? match.team2_score : ''}
                  placeholder="0"
                  id={`match-${match.id}-team2`}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '20px',
                    textAlign: 'center',
                    background: 'white',
                    border: '2px solid rgba(206, 212, 218, 0.6)',
                    color: '#0F172A',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Speichern Button */}
        <button
          onClick={() => {
            if (tournamentCompleted) return;

            if (tournament?.best_of_three === 1) {
              // Best of 3: Sammle Satz-Ergebnisse
              const team1Set1 = document.getElementById(`match-${match.id}-team1-set1`).value;
              const team2Set1 = document.getElementById(`match-${match.id}-team2-set1`).value;
              const team1Set2 = document.getElementById(`match-${match.id}-team1-set2`).value;
              const team2Set2 = document.getElementById(`match-${match.id}-team2-set2`).value;
              const team1Set3 = document.getElementById(`match-${match.id}-team1-set3`).value;
              const team2Set3 = document.getElementById(`match-${match.id}-team2-set3`).value;

              if (team1Set1 !== '' && team2Set1 !== '' && team1Set2 !== '' && team2Set2 !== '') {
                const scoreData = {
                  team1_set1: parseInt(team1Set1) || 0,
                  team2_set1: parseInt(team2Set1) || 0,
                  team1_set2: parseInt(team1Set2) || 0,
                  team2_set2: parseInt(team2Set2) || 0,
                  team1_set3: team1Set3 !== '' ? parseInt(team1Set3) : null,
                  team2_set3: team2Set3 !== '' ? parseInt(team2Set3) : null
                };
                handleUpdateMatch(match.id, scoreData);
              } else {
                setError(t('admin.errorEnterSets'));
                setTimeout(() => setError(''), 3000);
              }
            } else {
              // Best of 1: Nutze Scores
              const team1Score = document.getElementById(`match-${match.id}-team1`).value;
              const team2Score = document.getElementById(`match-${match.id}-team2`).value;
              if (team1Score && team2Score) {
                handleUpdateMatch(match.id, { team1_score: parseInt(team1Score), team2_score: parseInt(team2Score) });
              } else {
                setError(t('admin.errorEnterBothScores'));
                setTimeout(() => setError(''), 3000);
              }
            }
          }}
          style={{
            width: '100%',
            background: tournamentCompleted ? '#6b7280' :
                       match.status === 'beendet' ? '#e8b73a' : '#22c55e',
            color: '#0F172A',
            padding: '8px',
            border: 'none',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: tournamentCompleted ? 'not-allowed' : 'pointer',
            opacity: tournamentCompleted ? 0.6 : 1
          }}
          disabled={tournamentCompleted}
        >
          {tournamentCompleted ? t('admin.tournamentCompleted') : t('admin.saveResult')}
        </button>

        {match.status === 'beendet' && (
          <div style={{
            marginTop: '8px',
            textAlign: 'center',
            color: '#22c55e',
            fontWeight: 'bold',
            fontSize: '12px'
          }}>
            ✓ Beendet
          </div>
        )}

        {match.note && (
          <div style={{
            marginTop: '8px',
            padding: '6px 10px',
            background: '#fef9ec',
            border: '1px solid rgba(232,183,58,0.4)',
            borderRadius: '5px',
            fontSize: '12px',
            color: '#92611a',
            display: 'flex',
            gap: '6px',
            alignItems: 'flex-start'
          }}>
            <span>📝</span>
            <span>{match.note}</span>
          </div>
        )}
      </div>
    );
  };

  const renderMatchEditor = (match) => {
    return renderStandardMatchCard(match);
  };

  // eslint-disable-next-line no-unused-vars
  const renderGroupMatches = () => {
    // Group matches by group name
    const groups = {};
    matches.forEach(match => {
      if (match.round && match.round.startsWith('Gruppe ')) {
        const groupName = match.round;
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
        groups[groupName].push(match);
      }
    });

    return (
      <div>
        {Object.entries(groups).map(([groupName, groupMatches]) => (
          <div key={groupName} style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#e8b73a', marginBottom: '20px', fontSize: '1.5rem' }}>{groupName}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
              {groupMatches.map(match => renderMatchEditor(match))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderLandesligaMatches = () => {
    if (tournament.draw_generated !== 1) {
      return (
        <div className="card" style={{
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#fef3c7',
          border: '1px solid #e8b73a'
        }}>
          <p style={{ margin: 0, color: '#92400e' }}>
            🔒 Der Spielplan wird erst angezeigt, wenn das Turnier aktiviert wird.
          </p>
        </div>
      );
    }

    // Gruppiere Matches nach Vereins-Duell (match.round, z.B. "X3 vs. Garding")
    const duels = {};
    matches.forEach(match => {
      const duelName = match.round || 'Sonstige';
      if (!duels[duelName]) {
        duels[duelName] = [];
      }
      duels[duelName].push(match);
    });

    return (
      <div>
        {Object.entries(duels).map(([duelName, duelMatches]) => (
          <div key={duelName} style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#e8b73a', marginBottom: '20px', fontSize: '1.5rem' }}>{duelName}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
              {duelMatches.map(match => renderMatchEditor(match))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ── Drag & Drop Handler für manuelle Setzliste ───────────────────────
  const handleSeedDragStart = (e, source) => {
    setSeedDragSource(source);
  };

  const handleSeedDragOver = (e) => {
    e.preventDefault();
  };

  const handleSeedDrop = (e, slotIndex) => {
    e.preventDefault();
    if (!seedDragSource) return;

    const newSlots = [...(seedSlots || [])];

    if (seedDragSource.type === 'pool') {
      newSlots[slotIndex] = seedDragSource.teamId;
    } else if (seedDragSource.type === 'slot') {
      const temp = newSlots[slotIndex];
      newSlots[slotIndex] = newSlots[seedDragSource.slotIndex];
      newSlots[seedDragSource.slotIndex] = temp;
    }

    setSeedSlots(newSlots);
    setSeedDragSource(null);
  };

  const handleRemoveFromSeedSlot = (slotIndex) => {
    const newSlots = [...(seedSlots || [])];
    newSlots[slotIndex] = null;
    setSeedSlots(newSlots);
  };

  const handleGenerateBracket = async () => {
    if (!seedSlots || seedSlots.some(s => s === null)) return;
    setGeneratingBracket(true);
    setError('');
    try {
      await axios.post(`/api/tournaments/${tournament.id}/generate-draw-manual`, {
        team_order: seedSlots
      });
      setMessage('Bracket erfolgreich erstellt!');
      setTimeout(() => setMessage(''), 3000);
      await loadTournamentData(tournament.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Erstellen des Brackets');
    } finally {
      setGeneratingBracket(false);
    }
  };

  // ── KO mit Platzierungen ──────────────────────────────────────────────
  const renderKOPlacementView = () => {
    if (tournament.draw_generated !== 1) {
      // Manueller Modus: Setzliste per Drag & Drop
      if (tournament.draw_mode === 'manual') {
        const numSeeds = parseInt(tournament.num_teams);
        const MATCHUPS_8 = [[0,7], [3,4], [1,6], [2,5]];
        const MATCHUPS_16 = [[0,15], [7,8], [3,12], [4,11], [1,14], [6,9], [2,13], [5,10]];
        const MATCHUPS_32 = [[0,31], [15,16], [7,24], [8,23], [3,28], [12,19], [4,27], [11,20], [1,30], [14,17], [6,25], [9,22], [2,29], [13,18], [5,26], [10,21]];
        const matchups = numSeeds === 8 ? MATCHUPS_8 : numSeeds === 16 ? MATCHUPS_16 : MATCHUPS_32;

        const placedIds = new Set((seedSlots || []).filter(Boolean));
        const availableTeams = teams.filter(t => !placedIds.has(t.id));
        const allSeeded = seedSlots?.length === numSeeds && seedSlots.every(s => s !== null);

        const slotStyle = (filled) => ({
          minWidth: '160px',
          padding: '8px 12px',
          borderRadius: '6px',
          border: filled ? '2px solid #e8b73a' : '2px dashed #cbd5e1',
          background: filled ? 'rgba(232, 183, 58, 0.15)' : 'rgba(241, 245, 249, 0.5)',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background 0.15s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '6px',
        });

        return (
          <div>
            <div style={{ marginBottom: '20px', padding: '12px 16px', background: 'rgba(59,130,246,0.08)', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.2)' }}>
              <strong style={{ color: '#1d4ed8' }}>Manuelle Setzliste</strong>
              <p style={{ margin: '6px 0 0', color: '#475569', fontSize: '0.9rem' }}>
                Ziehe die Teams per Drag &amp; Drop in die Bracket-Slots. Seed 1 ist der Topgesetzte.
              </p>
            </div>

            {/* Verfügbare Teams (Pool) */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '10px', color: '#0F172A' }}>Nicht zugewiesen ({availableTeams.length})</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', minHeight: '44px', padding: '8px', background: 'rgba(241,245,249,0.6)', borderRadius: '8px', border: '1px dashed #94a3b8' }}>
                {availableTeams.length === 0 && (
                  <span style={{ color: '#94a3b8', fontSize: '0.85rem', alignSelf: 'center' }}>Alle Teams sind gesetzt</span>
                )}
                {availableTeams.map(team => (
                  <div
                    key={team.id}
                    draggable
                    onDragStart={(e) => handleSeedDragStart(e, { type: 'pool', teamId: team.id })}
                    style={{
                      padding: '6px 14px',
                      background: '#e8b73a',
                      color: '#0F172A',
                      borderRadius: '6px',
                      cursor: 'grab',
                      fontWeight: '500',
                      fontSize: '0.9rem',
                      userSelect: 'none',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    }}
                  >
                    {getDisplayName(team)}
                  </div>
                ))}
              </div>
            </div>

            {/* Matchup-Slots */}
            <h4 style={{ marginBottom: '12px', color: '#0F172A' }}>Erste Runde – Setzpaarungen</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
              {matchups.map(([s1, s2], matchIdx) => {
                const team1 = seedSlots?.[s1] ? teams.find(t => t.id === seedSlots[s1]) : null;
                const team2 = seedSlots?.[s2] ? teams.find(t => t.id === seedSlots[s2]) : null;
                return (
                  <div key={matchIdx} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ color: '#6c757d', fontSize: '0.8rem', minWidth: '56px', textAlign: 'right' }}>
                      Match {matchIdx + 1}
                    </span>

                    {/* Slot 1 */}
                    <div
                      onDragOver={handleSeedDragOver}
                      onDrop={(e) => handleSeedDrop(e, s1)}
                    >
                      <div
                        style={slotStyle(!!team1)}
                        draggable={!!team1}
                        onDragStart={team1 ? (e) => handleSeedDragStart(e, { type: 'slot', slotIndex: s1 }) : undefined}
                      >
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                          Seed {s1 + 1}:
                        </span>
                        {team1 ? (
                          <>
                            <span style={{ fontWeight: '500', fontSize: '0.88rem', flex: 1 }}>{getDisplayName(team1)}</span>
                            <button
                              onClick={() => handleRemoveFromSeedSlot(s1)}
                              title={t('common.remove')}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1rem', padding: '0 2px', lineHeight: 1 }}
                            >×</button>
                          </>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '0.82rem', flex: 1 }}>Hier ablegen…</span>
                        )}
                      </div>
                    </div>

                    <span style={{ color: '#6c757d', fontWeight: '600' }}>vs</span>

                    {/* Slot 2 */}
                    <div
                      onDragOver={handleSeedDragOver}
                      onDrop={(e) => handleSeedDrop(e, s2)}
                    >
                      <div
                        style={slotStyle(!!team2)}
                        draggable={!!team2}
                        onDragStart={team2 ? (e) => handleSeedDragStart(e, { type: 'slot', slotIndex: s2 }) : undefined}
                      >
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                          Seed {s2 + 1}:
                        </span>
                        {team2 ? (
                          <>
                            <span style={{ fontWeight: '500', fontSize: '0.88rem', flex: 1 }}>{getDisplayName(team2)}</span>
                            <button
                              onClick={() => handleRemoveFromSeedSlot(s2)}
                              title={t('common.remove')}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1rem', padding: '0 2px', lineHeight: 1 }}
                            >×</button>
                          </>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '0.82rem', flex: 1 }}>Hier ablegen…</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={handleGenerateBracket}
              disabled={!allSeeded || generatingBracket}
              style={{
                padding: '12px 28px',
                background: allSeeded && !generatingBracket ? '#22c55e' : '#94a3b8',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: allSeeded && !generatingBracket ? 'pointer' : 'not-allowed',
              }}
            >
              {generatingBracket ? t('admin.creatingBracket') : allSeeded ? t('admin.createBracket') : t('admin.seedTeamsRemaining', { n: numSeeds - (seedSlots?.filter(Boolean).length || 0) })}
            </button>
          </div>
        );
      }

      // Zufälliger Modus: Bracket erscheint bei Aktivierung
      return (
        <div className="card" style={{ padding: '20px', textAlign: 'center', backgroundColor: '#fef3c7', border: '1px solid #e8b73a' }}>
          <p style={{ margin: 0, color: '#92400e' }}>
            🔒 Der Bracket wird erst angezeigt, wenn das Turnier aktiviert wird.
          </p>
        </div>
      );
    }

    // Runden-Sortierung für das Placement-Bracket
    // eslint-disable-next-line no-unused-vars
    const roundOrder = {
      'Runde 1': 1,
      'Achtelfinale': 2,
      'Platz 17-32 Achtelfinale': 2,
      'Viertelfinale': 3,
      'Platz 9-16 Viertelfinale': 3,
      'Platz 17-24 Viertelfinale': 3,
      'Platz 25-32 Viertelfinale': 3,
      'Halbfinale': 4,
      'Platz 5-8 Halbfinale': 4,
      'Platz 9-12 Halbfinale': 4,
      'Platz 13-16 Halbfinale': 4,
      'Platz 17-20 Halbfinale': 4,
      'Platz 21-24 Halbfinale': 4,
      'Platz 25-28 Halbfinale': 4,
      'Platz 29-32 Halbfinale': 4,
      'Finale': 5,
      'Spiel um Platz 3': 5,
      'Spiel um Platz 5': 5,
      'Spiel um Platz 7': 5,
      'Spiel um Platz 9': 5,
      'Spiel um Platz 11': 5,
      'Spiel um Platz 13': 5,
      'Spiel um Platz 15': 5,
      'Spiel um Platz 17': 5,
      'Spiel um Platz 19': 5,
      'Spiel um Platz 21': 5,
      'Spiel um Platz 23': 5,
      'Spiel um Platz 25': 5,
      'Spiel um Platz 27': 5,
      'Spiel um Platz 29': 5,
      'Spiel um Platz 31': 5,
    };

    // Bracket-Abschnitte (Spalten)
    // Hauptbaum enthält bei 32 Teams eine zusätzliche Runde (Achtelfinale);
    // bei 8/16 Teams ist diese Runde in den Matches schlicht nicht vorhanden
    // und wird durch hasAnyMatches/activeSections automatisch ausgeblendet.
    const bracketSections = [
      {
        title: '🏆 Hauptbaum',
        color: '#e8b73a',
        rounds: ['Runde 1', 'Achtelfinale', 'Viertelfinale', 'Halbfinale', 'Finale', 'Spiel um Platz 3'],
      },
      {
        title: '🥈 Platz 5–8',
        color: '#fb923c',
        rounds: ['Platz 5-8 Halbfinale', 'Spiel um Platz 5', 'Spiel um Platz 7'],
      },
      {
        title: '🥉 Platz 9–12',
        color: '#60a5fa',
        rounds: ['Platz 9-16 Viertelfinale', 'Platz 9-12 Halbfinale', 'Spiel um Platz 9', 'Spiel um Platz 11'],
      },
      {
        title: '4️⃣ Platz 13–16',
        color: '#a78bfa',
        rounds: ['Platz 13-16 Halbfinale', 'Spiel um Platz 13', 'Spiel um Platz 15'],
      },
      {
        title: '5️⃣ Platz 17–20',
        color: '#f472b6',
        rounds: ['Platz 17-32 Achtelfinale', 'Platz 17-24 Viertelfinale', 'Platz 17-20 Halbfinale', 'Spiel um Platz 17', 'Spiel um Platz 19'],
      },
      {
        title: '6️⃣ Platz 21–24',
        color: '#38bdf8',
        rounds: ['Platz 21-24 Halbfinale', 'Spiel um Platz 21', 'Spiel um Platz 23'],
      },
      {
        title: '7️⃣ Platz 25–28',
        color: '#34d399',
        rounds: ['Platz 25-32 Viertelfinale', 'Platz 25-28 Halbfinale', 'Spiel um Platz 25', 'Spiel um Platz 27'],
      },
      {
        title: '8️⃣ Platz 29–32',
        color: '#facc15',
        rounds: ['Platz 29-32 Halbfinale', 'Spiel um Platz 29', 'Spiel um Platz 31'],
      },
    ];

    // Gruppiere alle Matches nach Rundenname
    const byRound = {};
    matches.forEach(m => {
      if (!byRound[m.round]) byRound[m.round] = [];
      byRound[m.round].push(m);
    });

    // Prüfe ob alle Runden einer Section leer sind
    const hasAnyMatches = (section) =>
      section.rounds.some(r => byRound[r] && byRound[r].length > 0);

    const activeSections = bracketSections.filter(hasAnyMatches);

    return (
      <div>
        {activeSections.map(section => (
          <div key={section.title} style={{ marginBottom: '40px' }}>
            <h2 style={{ color: section.color, marginBottom: '16px', fontSize: '1.2rem', borderBottom: `2px solid ${section.color}`, paddingBottom: '8px' }}>
              {section.title}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${section.rounds.filter(r => byRound[r]?.length > 0).length}, 1fr)`, gap: '20px' }}>
              {section.rounds.filter(r => byRound[r]?.length > 0).map(roundName => (
                <div key={roundName}>
                  <h3 style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px', textAlign: 'center' }}>
                    {roundName}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {byRound[roundName]
                      .sort((a, b) => a.match_number - b.match_number)
                      .map(match => {
                        const team1 = teams.find(t => t.id === match.team1_id);
                        const team2 = teams.find(t => t.id === match.team2_id);
                        return renderStandardMatchCard(match, team1, team2);
                      })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderKOMatches = () => {
    // Dynamisches Rundensystem - unterstützt Achtelfinale, Viertelfinale, Halbfinale, etc.
    const getRoundOrder = (roundName) => {
      // Definierte Runden haben feste Positionen (von links nach rechts)
      const fixedRounds = {
        'Achtelfinale': 10,
        'Viertelfinale': 20,
        'Halbfinale': 30,
        'Spiel um Platz 3': 100,  // Immer am Ende
        'Finale': 101,              // Immer am Ende
        'Trostrunde Finale': 1000   // Trostrunden-Finale ganz am Ende
      };

      if (fixedRounds[roundName] !== undefined) {
        return fixedRounds[roundName];
      }

      // Für generische "Runde X" - extrahiere die Nummer
      const rundeMatch = roundName.match(/^Runde (\d+)$/);
      if (rundeMatch) {
        return parseInt(rundeMatch[1], 10);
      }

      // Für Trostrunden "Trostrunde X" - extrahiere die Nummer
      const trostrundeMatch = roundName.match(/^Trostrunde (\d+)$/);
      if (trostrundeMatch) {
        return 200 + parseInt(trostrundeMatch[1], 10);  // 201, 202, 203, ...
      }

      // Fallback für unbekannte Runden
      return 99;
    };

    // Gruppiere Matches nach Runden (nur K.O.-Phase, keine Gruppenspiele und keine Platzierungsspiele)
    const rounds = {};
    matches.forEach(match => {
      // Filtere Gruppenspiele aus (alles was "Gruppe" im Namen hat)
      if (match.round && match.round.includes('Gruppe')) {
        return;
      }
      // Filtere Platzierungsspiele aus - nur Hauptturnier anzeigen
      if (match.placement_category && match.placement_category !== 'main') {
        return;
      }
      if (!rounds[match.round]) {
        rounds[match.round] = [];
      }
      rounds[match.round].push(match);
    });

    // Sortiere Matches innerhalb jeder Runde
    Object.keys(rounds).forEach(roundName => {
      rounds[roundName].sort((a, b) => (a.match_number || 0) - (b.match_number || 0));
    });

    // Sortiere Runden in richtiger Reihenfolge (von links nach rechts: Runde 1, Achtelfinale, Viertelfinale, ...)
    const sortedRoundNames = Object.keys(rounds).sort((a, b) => {
      return getRoundOrder(a) - getRoundOrder(b);
    });

    // Filtere Spiel um Platz 3, Finale und Trostrunden separat
    const mainRounds = sortedRoundNames.filter(r =>
      r !== 'Spiel um Platz 3' &&
      r !== 'Finale' &&
      !r.startsWith('Trostrunde')
    );
    const consolationRounds = sortedRoundNames.filter(r => r.startsWith('Trostrunde') && r !== 'Trostrunde Finale');
    const hasPlace3 = rounds['Spiel um Platz 3'] && rounds['Spiel um Platz 3'].length > 0;
    const hasFinal = rounds['Finale'] && rounds['Finale'].length > 0;
    const hasConsolationFinal = rounds['Trostrunde Finale'] && rounds['Trostrunde Finale'].length > 0;

    // Berechne Grid-Spalten dynamisch
    const numColumns = mainRounds.length + (hasFinal || hasPlace3 ? 1 : 0);

    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
        gap: '30px',
        marginTop: '20px'
      }}>
        {/* Dynamische Runden (Achtelfinale, Viertelfinale, Halbfinale) */}
        {mainRounds.map(roundName => (
          <div key={roundName} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <h3 style={{ color: '#e8b73a', marginBottom: '10px', textAlign: 'center' }}>{roundName}</h3>
            {rounds[roundName].map((match, index) => (
              <div key={match.id} style={{ marginBottom: '15px' }}>
                <div style={{ color: '#e8b73a', marginBottom: '5px', textAlign: 'center', fontSize: '14px' }}>
                  {roundName} {index + 1}
                </div>
                {renderMatchEditor(match)}
              </div>
            ))}
          </div>
        ))}

        {/* Finale & Spiel um Platz 3 Column */}
        {(hasFinal || hasPlace3) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Finale */}
            {hasFinal && (
              <div>
                <h3 style={{ color: '#e8b73a', marginBottom: '10px', textAlign: 'center' }}>Finale</h3>
                {rounds['Finale'].map((match, index) => (
                  <div key={match.id} style={{ marginBottom: '15px' }}>
                    <div style={{ color: '#e8b73a', marginBottom: '5px', textAlign: 'center', fontSize: '14px' }}>
                      Finale {index + 1}
                    </div>
                    <div className="card" style={{
                      padding: '15px',
                      background: 'rgba(232, 183, 58, 0.15)',
                      border: '2px solid #e8b73a',
                      minWidth: '250px'
                    }}>
                      {renderMatchEditor(match)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Spiel um Platz 3 */}
            {hasPlace3 && (
              <div>
                <h3 style={{ color: '#6c757d', marginBottom: '10px', textAlign: 'center' }}>Spiel um Platz 3</h3>
                {rounds['Spiel um Platz 3'].map((match, index) => (
                  <div key={match.id} style={{ marginBottom: '15px' }}>
                    <div style={{ color: '#6c757d', marginBottom: '5px', textAlign: 'center', fontSize: '14px' }}>
                      Spiel um Platz 3 {index + 1}
                    </div>
                    <div className="card" style={{
                      padding: '15px',
                      background: 'rgba(245, 242, 239, 0.7)',
                      border: '1px solid rgba(206, 212, 218, 0.5)',
                      minWidth: '250px'
                    }}>
                      {renderMatchEditor(match)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Trostrunden-Baum (falls vorhanden) */}
        {consolationRounds.length > 0 && (
          <div style={{ marginTop: '60px', borderTop: '3px solid #6c757d', paddingTop: '40px', gridColumn: '1 / -1' }}>
            <h2 style={{ color: '#6c757d', marginBottom: '30px', textAlign: 'center', fontSize: '1.8rem' }}>
              🏆 Trostrunde
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${consolationRounds.length + (hasConsolationFinal ? 1 : 0)}, 1fr)`,
              gap: '30px'
            }}>
              {/* Trostrunden */}
              {consolationRounds.map(roundName => (
                <div key={roundName} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <h3 style={{ color: '#6c757d', marginBottom: '10px', textAlign: 'center' }}>{roundName}</h3>
                  {rounds[roundName].map((match, index) => (
                    <div key={match.id} style={{ marginBottom: '15px' }}>
                      <div style={{ color: '#6c757d', marginBottom: '5px', textAlign: 'center', fontSize: '14px' }}>
                        {roundName} {index + 1}
                      </div>
                      {renderMatchEditor(match)}
                    </div>
                  ))}
                </div>
              ))}

              {/* Trostrunden-Finale */}
              {hasConsolationFinal && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <h3 style={{ color: '#6c757d', marginBottom: '10px', textAlign: 'center' }}>Trostrunde Finale</h3>
                  {rounds['Trostrunde Finale'].map((match, index) => (
                    <div key={match.id} style={{ marginBottom: '15px' }}>
                      <div style={{ color: '#6c757d', marginBottom: '5px', textAlign: 'center', fontSize: '14px' }}>
                        Finale {index + 1}
                      </div>
                      <div className="card" style={{
                        padding: '15px',
                        background: 'rgba(108, 117, 125, 0.1)',
                        border: '2px solid #6c757d',
                        minWidth: '250px'
                      }}>
                        {renderMatchEditor(match)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Trostrunden Rangliste (am Ende der Trostrunde) */}
            {tournamentCompleted && completionRankings?.consolation && completionRankings.consolation.length > 0 && (
              <div style={{ marginTop: '40px', textAlign: 'center' }}>
                <h3 style={{ color: '#6c757d', marginBottom: '20px', fontSize: '20px' }}>🏆 Endgültige Rangliste Trostrunde</h3>
                <div style={{
                  display: 'inline-block',
                  background: 'rgba(255, 255, 255, 0.95)',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '2px solid #6c757d',
                  minWidth: '350px'
                }}>
                  {completionRankings.consolation.map(ranking => (
                    <div key={ranking.place} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      padding: '10px 0',
                      borderBottom: ranking.place < 3 ? '1px solid rgba(206, 212, 218, 0.6)' : 'none'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: ranking.place === 1 ? '#d4af37' : ranking.place === 2 ? '#c0c0c0' : '#cd7f32',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#000',
                        fontWeight: 'bold',
                        fontSize: '18px'
                      }}>
                        {ranking.place}
                      </div>
                      <div style={{ flex: 1, textAlign: 'left', fontWeight: 'bold', color: '#0F172A' }}>
                        {ranking.team}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    );
  };

  // Render Mexicano/Americano view with matches and standings
  const renderMexicanoView = () => {
    const tournamentName = tournament.tournament_type === 'americano' ? 'Americano' : 'Mexicano';

    // Check if tournament has been activated
    if (tournament.draw_generated !== 1) {
      return (
        <div className="card" style={{
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#fef3c7',
          border: '1px solid #e8b73a'
        }}>
          <p style={{ margin: 0, color: '#92400e' }}>
            🔒 {tournamentName}-Paarungen werden erst angezeigt, wenn das Turnier aktiviert wird.
          </p>
        </div>
      );
    }

    // Group matches by round
    const rounds = {};
    matches.forEach(match => {
      if (match.round && match.round.startsWith('Runde ')) {
        if (!rounds[match.round]) {
          rounds[match.round] = [];
        }
        rounds[match.round].push(match);
      }
    });

    // Sort round names - NEUESTE RUNDE OBEN (absteigend)
    const sortedRoundNames = Object.keys(rounds).sort((a, b) => {
      const numA = parseInt(a.split(' ')[1]);
      const numB = parseInt(b.split(' ')[1]);
      return numB - numA;  // Umgekehrt: neueste Runde zuerst
    });

    // Check if current round is complete
    const currentRoundMatches = currentRound > 0 ? rounds[`Runde ${currentRound}`] : [];
    const currentRoundComplete = currentRoundMatches.length > 0 &&
      currentRoundMatches.every(match => match.status === 'beendet');

    return (
      <div>
        {/* Standings Section */}
        <div className="card" style={{
          marginBottom: '30px',
          background: 'rgba(232, 183, 58, 0.1)',
          border: '2px solid #e8b73a'
        }}>
          <h2 style={{ color: '#e8b73a', marginBottom: '20px', fontSize: '1.5rem', textAlign: 'center' }}>
            🏆 {tournamentName} Rangliste
          </h2>

          {mexicanoStandings.length > 0 ? (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '30px 1fr 40px 32px 32px 32px 42px',
                gap: '4px 6px',
                minWidth: '320px',
                margin: '0 auto'
              }}>
                {/* Headers */}
                <div style={{ fontWeight: 'bold', color: '#6c757d', fontSize: '0.7rem', paddingBottom: '4px', borderBottom: '2px solid #e8b73a', textAlign: 'center' }}>
                  #
                </div>
                <div style={{ fontWeight: 'bold', color: '#6c757d', fontSize: '0.7rem', paddingBottom: '4px', borderBottom: '2px solid #e8b73a' }}>
                  Spieler
                </div>
                <div style={{ fontWeight: 'bold', color: '#6c757d', fontSize: '0.7rem', textAlign: 'center', paddingBottom: '4px', borderBottom: '2px solid #e8b73a' }}>
                  Pkt.
                </div>
                <div style={{ fontWeight: 'bold', color: '#6c757d', fontSize: '0.7rem', textAlign: 'center', paddingBottom: '4px', borderBottom: '2px solid #e8b73a' }}>
                  S
                </div>
                <div style={{ fontWeight: 'bold', color: '#6c757d', fontSize: '0.7rem', textAlign: 'center', paddingBottom: '4px', borderBottom: '2px solid #e8b73a' }}>
                  U
                </div>
                <div style={{ fontWeight: 'bold', color: '#6c757d', fontSize: '0.7rem', textAlign: 'center', paddingBottom: '4px', borderBottom: '2px solid #e8b73a' }}>
                  N
                </div>
                <div style={{ fontWeight: 'bold', color: '#6c757d', fontSize: '0.7rem', textAlign: 'center', paddingBottom: '4px', borderBottom: '2px solid #e8b73a' }}>
                  Diff
                </div>

                {/* Player rows */}
                {mexicanoStandings.map((standing, index) => (
                  <React.Fragment key={standing.team_id}>
                    <div style={{
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      color: index === 0 ? '#d4af37' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : 'var(--text-primary)',
                      paddingTop: '8px',
                      textAlign: 'center'
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-primary)', fontWeight: '600', paddingTop: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {standing.player_name}
                    </div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#e8b73a', textAlign: 'center', paddingTop: '8px' }}>
                      {standing.points}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#22c55e', textAlign: 'center', paddingTop: '8px', fontWeight: '600' }}>
                      {standing.wins || 0}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d', textAlign: 'center', paddingTop: '8px' }}>
                      {standing.draws || 0}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#ef4444', textAlign: 'center', paddingTop: '8px', fontWeight: '600' }}>
                      {standing.losses || 0}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: (standing.goal_difference || 0) >= 0 ? '#22c55e' : '#ef4444',
                      textAlign: 'center',
                      paddingTop: '8px',
                      fontWeight: 'bold'
                    }}>
                      {(standing.goal_difference || 0) >= 0 ? `+${standing.goal_difference || 0}` : (standing.goal_difference || 0)}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: '#6c757d' }}>Noch keine Rangliste verfügbar</p>
          )}
        </div>

        {/* Current Round Info & Next Round Button */}
        {currentRound > 0 && (
          <div className="card" style={{
            marginBottom: '20px',
            background: currentRoundComplete ? 'rgba(34, 197, 94, 0.1)' : 'rgba(251, 146, 60, 0.1)',
            border: currentRoundComplete ? '2px solid #22c55e' : '2px solid #fb923c',
            textAlign: 'center'
          }}>
            <h3 style={{ marginBottom: '15px', color: 'var(--text-primary)' }}>
              Aktuelle Runde: {currentRound}{tournament.max_rounds ? ` / ${tournament.max_rounds}` : ''}
            </h3>

            {mexicanoCompleted ? (
              <div>
                <p style={{ marginBottom: '15px', color: '#166534', fontWeight: 'bold', fontSize: '1.2rem' }}>
                  🏆 {tournamentName}-Turnier abgeschlossen!
                </p>
                <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                  Alle Ergebnisse wurden gespeichert. Die finale Rangliste wird oben angezeigt.
                </p>
              </div>
            ) : currentRoundComplete ? (
              <div>
                {/* Prüfe ob letzte Runde erreicht ist */}
                {tournament.max_rounds && currentRound >= tournament.max_rounds ? (
                  <>
                    <p style={{ marginBottom: '15px', color: '#166534', fontWeight: 'bold' }}>
                      ✓ Alle {tournament.max_rounds} Runden abgeschlossen!
                    </p>
                    <button
                      onClick={() => {
                        setMexicanoCompleted(true);
                        setMessage(`🏆 ${tournamentName}-Turnier erfolgreich abgeschlossen!`);
                        setTimeout(() => setMessage(''), 5000);
                      }}
                      style={{
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #d4af37, #b8860b)',
                        color: '#0F172A',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      🏆 {tournamentName} abschließen
                    </button>
                  </>
                ) : (
                  <>
                    <p style={{ marginBottom: '15px', color: '#166534', fontWeight: 'bold' }}>
                      ✓ Alle Matches dieser Runde sind abgeschlossen!
                    </p>
                    <button
                      onClick={tournament.tournament_type === 'americano' ? handleGenerateNextAmericanoRound : handleGenerateNextMexicanoRound}
                      disabled={generatingNextRound}
                      style={{
                        padding: '12px 24px',
                        background: generatingNextRound ? '#6b7280' : '#22c55e',
                        color: '#0F172A',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        cursor: generatingNextRound ? 'wait' : 'pointer',
                        opacity: generatingNextRound ? 0.6 : 1
                      }}
                    >
                      {generatingNextRound ? t('admin.generating') : t('admin.generateNextRound', { n: currentRound + 1 })}
                    </button>
                  </>
                )}
              </div>
            ) : (
              <p style={{ margin: 0, color: '#92400e' }}>
                ⏳ Bitte alle Matches dieser Runde abschließen, um die nächste Runde zu generieren.
              </p>
            )}
          </div>
        )}

        {/* Matches by Round */}
        {sortedRoundNames.length > 0 ? (
          sortedRoundNames.map(roundName => (
            <div key={roundName} style={{ marginBottom: '40px' }}>
              <h2 style={{ color: '#e8b73a', marginBottom: '20px', fontSize: '1.5rem' }}>
                {roundName}
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
                {rounds[roundName]
                  .sort((a, b) => a.match_number - b.match_number)
                  .map(match => {
                    // Get all 4 players
                    // Team 1 (team1_score): player1 + player3 (team1_id + team3_id)
                    // Team 2 (team2_score): player2 + player4 (team2_id + team4_id)
                    const player1 = teams.find(t => t.id === match.team1_id);
                    const player2 = teams.find(t => t.id === match.team2_id);
                    const player3 = teams.find(t => t.id === match.team3_id);
                    const player4 = teams.find(t => t.id === match.team4_id);

                    return (
                      <div key={match.id} id={`match-${match.id}`} className="card" style={{
                        padding: '15px',
                        border: match.status === 'beendet' ? '2px solid #22c55e' : '1px solid rgba(206, 212, 218, 0.5)',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)'
                      }}>
                        {/* Court info + Edit-Button */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <div style={{ fontSize: '0.8rem', color: '#6c757d', fontWeight: 'bold' }}>
                            {match.court && <span>🏟️ {match.court}</span>}
                            {match.start_time && <span style={{ marginLeft: match.court ? '8px' : 0 }}>⏰ {match.start_time}</span>}
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingMatch({ id: match.id, court: match.court || '', start_time: match.start_time || '', note: match.note || '' }); }}
                            title={t('admin.editCourtTime')}
                            style={{
                              background: 'none', border: '1px solid rgba(206,212,218,0.5)', borderRadius: '4px',
                              cursor: 'pointer', fontSize: '0.85rem', padding: '2px 7px', color: '#6c757d'
                            }}
                          >✏️</button>
                        </div>

                        {/* Team 1 (Player 1 + Player 3) */}
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '5px' }}>
                            Team 1:
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                            <div style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                              • {player1 ? getDisplayName(player1) : t('edit.player1')}
                            </div>
                            <div style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                              • {player3 ? getDisplayName(player3) : t('edit.playerN', { n: 3 })}
                            </div>
                          </div>
                          <input
                            type="number"
                            min="0"
                            key={`match-${match.id}-team1-${match.team1_score}`}
                            defaultValue={match.team1_score !== null ? match.team1_score : ''}
                            placeholder={t('admin.pointsTeam1')}
                            id={`match-${match.id}-team1`}
                            disabled={mexicanoCompleted}
                            style={{
                              width: '100%',
                              padding: '8px',
                              fontSize: '16px',
                              textAlign: 'center',
                              background: mexicanoCompleted ? 'var(--input-disabled-bg)' : 'var(--input-bg)',
                              border: '1px solid var(--input-border)',
                              color: mexicanoCompleted ? 'var(--input-disabled-color)' : 'var(--text-primary)',
                              borderRadius: '4px',
                              cursor: mexicanoCompleted ? 'not-allowed' : 'text',
                              opacity: mexicanoCompleted ? 0.6 : 1
                            }}
                          />
                        </div>

                        <div style={{
                          textAlign: 'center',
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          color: '#e8b73a',
                          margin: '10px 0'
                        }}>
                          VS
                        </div>

                        {/* Team 2 (Player 2 + Player 4) */}
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '5px' }}>
                            Team 2:
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                            <div style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                              • {player2 ? getDisplayName(player2) : t('edit.player2')}
                            </div>
                            <div style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                              • {player4 ? getDisplayName(player4) : t('edit.playerN', { n: 4 })}
                            </div>
                          </div>
                          <input
                            type="number"
                            min="0"
                            key={`match-${match.id}-team2-${match.team2_score}`}
                            defaultValue={match.team2_score !== null ? match.team2_score : ''}
                            placeholder={t('admin.pointsTeam2')}
                            id={`match-${match.id}-team2`}
                            disabled={mexicanoCompleted}
                            style={{
                              width: '100%',
                              padding: '8px',
                              fontSize: '16px',
                              textAlign: 'center',
                              background: mexicanoCompleted ? 'var(--input-disabled-bg)' : 'var(--input-bg)',
                              border: '1px solid var(--input-border)',
                              color: mexicanoCompleted ? 'var(--input-disabled-color)' : 'var(--text-primary)',
                              borderRadius: '4px',
                              cursor: mexicanoCompleted ? 'not-allowed' : 'text',
                              opacity: mexicanoCompleted ? 0.6 : 1
                            }}
                          />
                        </div>

                        <button
                          onClick={() => {
                            if (mexicanoCompleted) return;
                            const team1Score = document.getElementById(`match-${match.id}-team1`).value;
                            const team2Score = document.getElementById(`match-${match.id}-team2`).value;
                            if (team1Score && team2Score) {
                              handleUpdateMatch(match.id, {
                                team1_score: parseInt(team1Score),
                                team2_score: parseInt(team2Score)
                              });
                            } else {
                              setError(t('admin.errorEnterBothScores'));
                              setTimeout(() => setError(''), 3000);
                            }
                          }}
                          disabled={mexicanoCompleted}
                          style={{
                            width: '100%',
                            background: mexicanoCompleted ? '#6b7280' : '#22c55e',
                            color: '#0F172A',
                            padding: '10px',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: mexicanoCompleted ? 'not-allowed' : 'pointer',
                            opacity: mexicanoCompleted ? 0.6 : 1
                          }}
                        >
                          {mexicanoCompleted ? t('admin.tournamentFinished') : t('admin.saveResult')}
                        </button>

                        {match.status === 'beendet' && (
                          <div style={{
                            marginTop: '10px',
                            textAlign: 'center',
                            color: '#22c55e',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}>
                            ✓ Beendet
                          </div>
                        )}

                        {match.note && (
                          <div style={{
                            marginTop: '8px',
                            padding: '6px 10px',
                            background: '#fef9ec',
                            border: '1px solid rgba(232,183,58,0.4)',
                            borderRadius: '5px',
                            fontSize: '12px',
                            color: '#92611a',
                            display: 'flex',
                            gap: '6px',
                            alignItems: 'flex-start'
                          }}>
                            <span>📝</span>
                            <span>{match.note}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          ))
        ) : (
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <p style={{ color: '#6c757d' }}>Keine Matches vorhanden. Bitte Turnier aktivieren.</p>
          </div>
        )}
      </div>
    );
  };

  // New function to render groups overview
  const renderGroupsOverview = () => {
    // Only show groups if tournament has been activated at least once
    if (tournament.draw_generated !== 1) {
      return (
        <div className="card" style={{
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#fef3c7',
          border: '1px solid #e8b73a'
        }}>
          <p style={{ margin: 0, color: '#92400e' }}>
            🔒 Gruppen werden erst angezeigt, wenn das Turnier aktiviert wird.
          </p>
        </div>
      );
    }

    // Group teams by group name
    const groups = {};
    teams.forEach(team => {
      if (team.group_name) {
        if (!groups[team.group_name]) {
          groups[team.group_name] = [];
        }
        groups[team.group_name].push(team);
      }
    });

    // Sort group names alphabetically
    const sortedGroupNames = Object.keys(groups).sort();

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
        {sortedGroupNames.map(groupName => {
          const groupTeams = groups[groupName];
          return (
            <div key={groupName} className="card" style={{
              padding: '20px',
              backgroundColor: 'rgba(245, 242, 239, 0.95)',
              border: '2px solid rgba(206, 212, 218, 0.6)',
              borderRadius: '8px'
            }}>
              <h4 style={{
                marginTop: '0',
                marginBottom: '20px',
                color: '#e8b73a',
                fontSize: '1.4rem',
                fontWeight: 'bold',
                textAlign: 'center'
              }}>
                {groupName} - Rangliste
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                {/* Column headers */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: tournament.best_of_three === 1
                    ? '1fr 70px 70px 70px 90px 90px'
                    : '1fr 70px 70px 70px 70px 70px',
                  gap: '16px',
                  padding: '12px 16px',
                  fontWeight: 'bold',
                  color: '#495057',
                  fontSize: '1rem',
                  borderBottom: '2px solid rgba(206, 212, 218, 0.8)',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)'
                }}>
                  <div>Team</div>
                  <div style={{ textAlign: 'center' }}>Pkt.</div>
                  <div style={{ textAlign: 'center' }}>Win</div>
                  <div style={{ textAlign: 'center' }}>Loss</div>
                  {tournament.best_of_three === 1 ? (
                    <>
                      <div style={{ textAlign: 'center' }}>Sätze</div>
                      <div style={{ textAlign: 'center' }}>Games</div>
                    </>
                  ) : (
                    <>
                      <div style={{ textAlign: 'center' }}>Draw</div>
                      <div style={{ textAlign: 'center' }}>Dif.</div>
                    </>
                  )}
                </div>

                {groupTeams.map(team => {
                  // Calculate team stats from matches
                  const teamMatches = matches.filter(match =>
                    (match.team1_id === team.id || match.team2_id === team.id) &&
                    match.round === groupName &&
                    match.status === 'beendet'
                  );

                  let points = 0;
                  let wins = 0;
                  let losses = 0;
                  let draws = 0;
                  let goalsFor = 0;
                  let goalsAgainst = 0;
                  let setsFor = 0;
                  let setsAgainst = 0;
                  let gamesFor = 0;
                  let gamesAgainst = 0;

                  teamMatches.forEach(match => {
                    // Best of 3: Count sets AND games
                    if (tournament.best_of_three === 1) {
                      let team1Sets = 0;
                      let team2Sets = 0;
                      let team1Games = 0;
                      let team2Games = 0;

                      // Count set 1
                      if (match.team1_set1 != null && match.team2_set1 != null) {
                        team1Games += match.team1_set1;
                        team2Games += match.team2_set1;
                        if (match.team1_set1 > match.team2_set1) team1Sets++;
                        else if (match.team2_set1 > match.team1_set1) team2Sets++;
                      }

                      // Count set 2
                      if (match.team1_set2 != null && match.team2_set2 != null) {
                        team1Games += match.team1_set2;
                        team2Games += match.team2_set2;
                        if (match.team1_set2 > match.team2_set2) team1Sets++;
                        else if (match.team2_set2 > match.team1_set2) team2Sets++;
                      }

                      // Count set 3 (tiebreak) - only if actually played (not both 0)
                      if (match.team1_set3 != null && match.team2_set3 != null && (match.team1_set3 > 0 || match.team2_set3 > 0)) {
                        team1Games += match.team1_set3;
                        team2Games += match.team2_set3;
                        if (match.team1_set3 > match.team2_set3) team1Sets++;
                        else if (match.team2_set3 > match.team1_set3) team2Sets++;
                      }

                      // Determine winner based on sets
                      if (match.team1_id === team.id) {
                        setsFor += team1Sets;
                        setsAgainst += team2Sets;
                        gamesFor += team1Games;
                        gamesAgainst += team2Games;
                        if (team1Sets > team2Sets) {
                          points += 3;
                          wins += 1;
                        } else if (team2Sets > team1Sets) {
                          losses += 1;
                        }
                        // No draws in Best of 3
                      } else if (match.team2_id === team.id) {
                        setsFor += team2Sets;
                        setsAgainst += team1Sets;
                        gamesFor += team2Games;
                        gamesAgainst += team1Games;
                        if (team2Sets > team1Sets) {
                          points += 3;
                          wins += 1;
                        } else if (team1Sets > team2Sets) {
                          losses += 1;
                        }
                        // No draws in Best of 3
                      }
                    } else {
                      // Best of 1: Use old logic with game scores
                      const t1_score = match.team1_score || 0;
                      const t2_score = match.team2_score || 0;

                      if (match.team1_id === team.id) {
                        goalsFor += t1_score;
                        goalsAgainst += t2_score;
                        if (t1_score > t2_score) {
                          points += 3;
                          wins += 1;
                        } else if (t2_score > t1_score) {
                          losses += 1;
                        } else {
                          points += 1;
                          draws += 1;
                        }
                      } else if (match.team2_id === team.id) {
                        goalsFor += t2_score;
                        goalsAgainst += t1_score;
                        if (t2_score > t1_score) {
                          points += 3;
                          wins += 1;
                        } else if (t1_score > t2_score) {
                          losses += 1;
                        } else {
                          points += 1;
                          draws += 1;
                        }
                      }
                    }
                  });

                  const goalDifference = goalsFor - goalsAgainst;
                  const setsDifference = setsFor - setsAgainst;
                  const gamesDifference = gamesFor - gamesAgainst;

                  return {
                    team,
                    points,
                    wins,
                    losses,
                    draws,
                    goalDifference,
                    goalsFor,
                    goalsAgainst,
                    setsFor,
                    setsAgainst,
                    setsDifference,
                    gamesFor,
                    gamesAgainst,
                    gamesDifference
                  };
                })
                // Sort teams
                .sort((a, b) => {
                  // 1. Points (Gewonnene Spiele)
                  if (b.points !== a.points) return b.points - a.points;

                  if (tournament.best_of_three === 1) {
                    // 2. Satzdifferenz
                    if (b.setsDifference !== a.setsDifference) return b.setsDifference - a.setsDifference;
                    // 3. Games-Differenz
                    if (b.gamesDifference !== a.gamesDifference) return b.gamesDifference - a.gamesDifference;
                    // 4. Direkter Vergleich (TODO: implement if needed)
                  } else {
                    // Best of 1: old logic
                    if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
                    return b.goalsFor - a.goalsFor;
                  }

                  return 0;
                })

                .map((teamData, index) => (
                  <div key={teamData.team.id} style={{
                    display: 'grid',
                    gridTemplateColumns: tournament.best_of_three === 1
                      ? '1fr 70px 70px 70px 90px 90px'
                      : '1fr 70px 70px 70px 70px 70px',
                    gap: '16px',
                    padding: '14px 16px',
                    borderBottom: index < groupTeams.length - 1 ? '1px solid rgba(206, 212, 218, 0.4)' : 'none',
                    backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.6)' : 'transparent',
                    borderRadius: '4px'
                  }}>
                    <div style={{ fontWeight: '600', color: '#0F172A', fontSize: '0.95rem' }}>
                      {getDisplayName(teamData.team)}
                    </div>
                    <div style={{ textAlign: 'center', color: '#0F172A', fontWeight: 'bold', fontSize: '1rem' }}>{teamData.points}</div>
                    <div style={{ textAlign: 'center', color: '#22c55e', fontWeight: '600', fontSize: '0.95rem' }}>{teamData.wins}</div>
                    <div style={{ textAlign: 'center', color: '#ef4444', fontWeight: '600', fontSize: '0.95rem' }}>{teamData.losses}</div>
                    {tournament.best_of_three === 1 ? (
                      <>
                        <div style={{ textAlign: 'center', color: '#495057', fontWeight: '600', fontSize: '0.95rem' }}>
                          {teamData.setsFor}-{teamData.setsAgainst}
                        </div>
                        <div style={{ textAlign: 'center', color: '#495057', fontWeight: '600', fontSize: '0.95rem' }}>
                          {teamData.gamesFor}-{teamData.gamesAgainst}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.95rem' }}>{teamData.draws}</div>
                        <div style={{ textAlign: 'center', color: '#495057', fontWeight: '600', fontSize: '0.95rem' }}>
                          {teamData.goalDifference >= 0 ? `+${teamData.goalDifference}` : teamData.goalDifference}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Group matches */}
              <div style={{ marginTop: '20px' }}>
                <h5 style={{ color: '#6c757d', fontSize: '1rem', marginBottom: '12px' }}>Gruppenspiele</h5>
                {matches
                  .filter(match => match.round === groupName)
                  .sort((a, b) => {
                    // Sort by time if schedule is available
                    if (schedule.length > 0) {
                      const scheduleA = schedule.find(s => s.match_id === a.id);
                      const scheduleB = schedule.find(s => s.match_id === b.id);
                      if (scheduleA && scheduleB) {
                        return scheduleA.start_time.localeCompare(scheduleB.start_time);
                      }
                    }
                    // Fallback to match_number
                    return a.match_number - b.match_number;
                  })
                  .map(match => {
                    const team1 = teams.find(t => t.id === match.team1_id);
                    const team2 = teams.find(t => t.id === match.team2_id);
                    const matchSchedule = schedule.find(s => s.match_id === match.id);

                    return (
                      <div id={`match-${match.id}`} key={`group-${groupName}-match-${match.match_number}`} style={{
                        padding: '15px',
                        marginBottom: '12px',
                        borderRadius: '6px',
                        border: match.status === 'beendet' ? '2px solid #22c55e' : '1px solid rgba(206, 212, 218, 0.5)',
                        backgroundColor: match.status === 'beendet' ? 'rgba(34, 197, 94, 0.05)' : 'white',
                        transition: 'border 0.3s ease'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div style={{ fontSize: '0.75rem', color: '#6c757d', fontWeight: '500' }}>
                            {(match.court || matchSchedule?.court) && (
                              <span>🏟️ {match.court || matchSchedule?.court}</span>
                            )}
                            {(match.start_time || matchSchedule) && (
                              <span style={{ marginLeft: (match.court || matchSchedule?.court) ? '8px' : 0, color: '#e8b73a' }}>
                                ⏰ {match.start_time || matchSchedule?.start_time}
                                {match.start_time
                                  ? (calcEndTime(match.start_time) ? ` - ${calcEndTime(match.start_time)}` : '')
                                  : (matchSchedule?.end_time ? ` - ${matchSchedule.end_time}` : '')}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingMatch({ id: match.id, court: match.court || '', start_time: match.start_time || '', note: match.note || '' }); }}
                            title={t('admin.editCourtTime')}
                            style={{
                              background: 'none', border: '1px solid rgba(206,212,218,0.5)', borderRadius: '4px',
                              cursor: 'pointer', fontSize: '0.85rem', padding: '2px 7px', color: '#6c757d'
                            }}
                          >✏️</button>
                        </div>

                        {/* Team Namen nebeneinander */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto 1fr',
                          gap: '12px',
                          marginBottom: '12px',
                          alignItems: 'center'
                        }}>
                          <div style={{ color: '#0F172A', fontWeight: '600', fontSize: '14px', textAlign: 'left' }}>
                            {team1 ? getDisplayName(team1) : getTeamPlaceholder(match, 'team1')}
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#9ca3af' }}>vs</div>
                          <div style={{ color: '#0F172A', fontWeight: '600', fontSize: '14px', textAlign: 'right' }}>
                            {team2 ? getDisplayName(team2) : getTeamPlaceholder(match, 'team2')}
                          </div>
                        </div>

                        {/* Best of 3 oder Best of 1 Eingabe */}
                        {tournament?.best_of_three === 1 ? (
                          /* Best of 3 - Spalten Layout */
                          <div>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '12px',
                              marginBottom: '12px'
                            }}>
                              {/* Team 1 Spalte */}
                              <div style={{
                                padding: '10px',
                                background: 'rgba(245, 242, 239, 0.4)',
                                borderRadius: '4px'
                              }}>
                                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '6px', fontWeight: '600' }}>
                                  {team1 ? team1.players.map(p => p.name.split(' ')[0]).join('/') : 'Team 1'}
                                </div>
                                <div style={{ marginBottom: '6px' }}>
                                  <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '2px' }}>{t('admin.set1')}:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="7"
                                    key={`${match.id}-team1-set1-${match.team1_set1}`}
                                    defaultValue={match.team1_set1 !== null ? match.team1_set1 : ''}
                                    placeholder="0"
                                    id={`match-${match.id}-team1-set1`}
                                    style={{
                                      width: '100%',
                                      padding: '6px',
                                      fontSize: '14px',
                                      textAlign: 'center',
                                      background: 'white',
                                      border: '1px solid rgba(206, 212, 218, 0.5)',
                                      color: '#0F172A',
                                      borderRadius: '4px',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                </div>
                                <div style={{ marginBottom: '6px' }}>
                                  <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '2px' }}>{t('admin.set2')}:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="7"
                                    key={`${match.id}-team1-set2-${match.team1_set2}`}
                                    defaultValue={match.team1_set2 !== null ? match.team1_set2 : ''}
                                    placeholder="0"
                                    id={`match-${match.id}-team1-set2`}
                                    style={{
                                      width: '100%',
                                      padding: '6px',
                                      fontSize: '14px',
                                      textAlign: 'center',
                                      background: 'white',
                                      border: '1px solid rgba(206, 212, 218, 0.5)',
                                      color: '#0F172A',
                                      borderRadius: '4px',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: '11px', color: '#e8b73a', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>{t('admin.tiebreak')}:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="20"
                                    key={`${match.id}-team1-set3-${match.team1_set3}`}
                                    defaultValue={match.team1_set3 !== null && match.team1_set3 !== 0 ? match.team1_set3 : ''}
                                    placeholder="0"
                                    id={`match-${match.id}-team1-set3`}
                                    style={{
                                      width: '100%',
                                      padding: '6px',
                                      fontSize: '14px',
                                      textAlign: 'center',
                                      background: 'rgba(254, 243, 199, 0.3)',
                                      border: '1px solid #e8b73a',
                                      color: '#0F172A',
                                      borderRadius: '4px',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                </div>
                              </div>

                              {/* Team 2 Spalte */}
                              <div style={{
                                padding: '10px',
                                background: 'rgba(245, 242, 239, 0.4)',
                                borderRadius: '4px'
                              }}>
                                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '6px', fontWeight: '600' }}>
                                  {team2 ? team2.players.map(p => p.name.split(' ')[0]).join('/') : 'Team 2'}
                                </div>
                                <div style={{ marginBottom: '6px' }}>
                                  <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '2px' }}>{t('admin.set1')}:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="7"
                                    key={`${match.id}-team2-set1-${match.team2_set1}`}
                                    defaultValue={match.team2_set1 !== null ? match.team2_set1 : ''}
                                    placeholder="0"
                                    id={`match-${match.id}-team2-set1`}
                                    style={{
                                      width: '100%',
                                      padding: '6px',
                                      fontSize: '14px',
                                      textAlign: 'center',
                                      background: 'white',
                                      border: '1px solid rgba(206, 212, 218, 0.5)',
                                      color: '#0F172A',
                                      borderRadius: '4px',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                </div>
                                <div style={{ marginBottom: '6px' }}>
                                  <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '2px' }}>{t('admin.set2')}:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="7"
                                    key={`${match.id}-team2-set2-${match.team2_set2}`}
                                    defaultValue={match.team2_set2 !== null ? match.team2_set2 : ''}
                                    placeholder="0"
                                    id={`match-${match.id}-team2-set2`}
                                    style={{
                                      width: '100%',
                                      padding: '6px',
                                      fontSize: '14px',
                                      textAlign: 'center',
                                      background: 'white',
                                      border: '1px solid rgba(206, 212, 218, 0.5)',
                                      color: '#0F172A',
                                      borderRadius: '4px',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: '11px', color: '#e8b73a', display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>{t('admin.tiebreak')}:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="20"
                                    key={`${match.id}-team2-set3-${match.team2_set3}`}
                                    defaultValue={match.team2_set3 !== null && match.team2_set3 !== 0 ? match.team2_set3 : ''}
                                    placeholder="0"
                                    id={`match-${match.id}-team2-set3`}
                                    style={{
                                      width: '100%',
                                      padding: '6px',
                                      fontSize: '14px',
                                      textAlign: 'center',
                                      background: 'rgba(254, 243, 199, 0.3)',
                                      border: '1px solid #e8b73a',
                                      color: '#0F172A',
                                      borderRadius: '4px',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                </div>
                              </div>
                            </div>

                            <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '8px', textAlign: 'center', fontStyle: 'italic' }}>
                              {t('admin.tiebreakHint')}
                            </div>

                            <button
                              onClick={() => {
                                if (tournamentCompleted) return;
                                const team1Set1 = document.getElementById(`match-${match.id}-team1-set1`).value;
                                const team2Set1 = document.getElementById(`match-${match.id}-team2-set1`).value;
                                const team1Set2 = document.getElementById(`match-${match.id}-team1-set2`).value;
                                const team2Set2 = document.getElementById(`match-${match.id}-team2-set2`).value;
                                const team1Set3 = document.getElementById(`match-${match.id}-team1-set3`).value;
                                const team2Set3 = document.getElementById(`match-${match.id}-team2-set3`).value;

                                if (team1Set1 !== '' && team2Set1 !== '' && team1Set2 !== '' && team2Set2 !== '') {
                                  const scoreData = {
                                    team1_set1: parseInt(team1Set1) || 0,
                                    team2_set1: parseInt(team2Set1) || 0,
                                    team1_set2: parseInt(team1Set2) || 0,
                                    team2_set2: parseInt(team2Set2) || 0,
                                    team1_set3: team1Set3 !== '' ? parseInt(team1Set3) : null,
                                    team2_set3: team2Set3 !== '' ? parseInt(team2Set3) : null
                                  };
                                  handleUpdateMatch(match.id, scoreData);
                                } else {
                                  setError(t('admin.errorEnterSets'));
                                  setTimeout(() => setError(''), 3000);
                                }
                              }}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                background: tournamentCompleted ? '#6b7280' :
                                           match.status === 'beendet' ? '#e8b73a' : '#22c55e',
                                border: 'none',
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                cursor: tournamentCompleted ? 'not-allowed' : 'pointer',
                                opacity: tournamentCompleted ? 0.6 : 1
                              }}
                              disabled={tournamentCompleted}
                            >
                              {tournamentCompleted ? t('admin.tournamentCompleted') : t('common.save')}
                            </button>
                          </div>
                        ) : (
                          /* Best of 1 - Spalten Layout */
                          <div>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '12px',
                              marginBottom: '12px'
                            }}>
                              <div style={{
                                padding: '10px',
                                background: 'rgba(245, 242, 239, 0.4)',
                                borderRadius: '4px'
                              }}>
                                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '6px', fontWeight: '600' }}>
                                  {team1 ? team1.players.map(p => p.name.split(' ')[0]).join('/') : 'Team 1'}
                                </div>
                                <input
                                  type="number"
                                  min="0"
                                  key={`${match.id}-team1-score-${match.team1_score}`}
                                  defaultValue={match.team1_score !== null ? match.team1_score : ''}
                                  placeholder="0"
                                  id={`match-${match.id}-team1`}
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    fontSize: '16px',
                                    textAlign: 'center',
                                    background: 'white',
                                    border: '1px solid rgba(206, 212, 218, 0.5)',
                                    color: '#0F172A',
                                    borderRadius: '4px',
                                    fontWeight: 'bold'
                                  }}
                                />
                              </div>

                              <div style={{
                                padding: '10px',
                                background: 'rgba(245, 242, 239, 0.4)',
                                borderRadius: '4px'
                              }}>
                                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '6px', fontWeight: '600' }}>
                                  {team2 ? team2.players.map(p => p.name.split(' ')[0]).join('/') : 'Team 2'}
                                </div>
                                <input
                                  type="number"
                                  min="0"
                                  key={`${match.id}-team2-score-${match.team2_score}`}
                                  defaultValue={match.team2_score !== null ? match.team2_score : ''}
                                  placeholder="0"
                                  id={`match-${match.id}-team2`}
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    fontSize: '16px',
                                    textAlign: 'center',
                                    background: 'white',
                                    border: '1px solid rgba(206, 212, 218, 0.5)',
                                    color: '#0F172A',
                                    borderRadius: '4px',
                                    fontWeight: 'bold'
                                  }}
                                />
                              </div>
                            </div>

                            <button
                              onClick={() => {
                                if (tournamentCompleted) return;
                                const team1Score = document.getElementById(`match-${match.id}-team1`).value;
                                const team2Score = document.getElementById(`match-${match.id}-team2`).value;
                                if (team1Score && team2Score) {
                                  handleUpdateMatch(match.id, { team1_score: parseInt(team1Score), team2_score: parseInt(team2Score) });
                                } else {
                                  setError(t('admin.errorEnterBothScores'));
                                  setTimeout(() => setError(''), 3000);
                                }
                              }}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                background: tournamentCompleted ? '#6b7280' :
                                           match.status === 'beendet' ? '#e8b73a' : '#22c55e',
                                border: 'none',
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                cursor: tournamentCompleted ? 'not-allowed' : 'pointer',
                                opacity: tournamentCompleted ? 0.6 : 1
                              }}
                              disabled={tournamentCompleted}
                            >
                              {tournamentCompleted ? t('admin.tournamentCompleted') : t('common.save')}
                            </button>
                          </div>
                        )}

                        {match.note && (
                          <div style={{
                            marginTop: '8px',
                            padding: '6px 10px',
                            background: '#fef9ec',
                            border: '1px solid rgba(232,183,58,0.4)',
                            borderRadius: '5px',
                            fontSize: '12px',
                            color: '#92611a',
                            display: 'flex',
                            gap: '6px',
                            alignItems: 'flex-start'
                          }}>
                            <span>📝</span>
                            <span>{match.note}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render Zeitplan Tab mit Bearbeitungsmöglichkeit
  const renderScheduleTab = () => {
    if (!schedule || schedule.length === 0) {
      return (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', color: '#e8b73a' }}>⏰</div>
          <h3 style={{ color: '#0F172A', marginBottom: '1rem' }}>Kein Zeitplan verfügbar</h3>
          <p style={{ color: '#64748b' }}>
            Für dieses Turnier wurde keine Zeitplanung konfiguriert.
          </p>
        </div>
      );
    }

    // Formatiere Zeit
    const formatTime = (timeString) => timeString || '--:--';

    // Hole Match-Daten
    const getMatchResult = (matchId) => {
      const match = matches.find(m => m.id === matchId);
      if (!match || match.status !== 'beendet') return null;

      if (match.team1_set1 !== null) {
        const sets = [];
        sets.push(`${match.team1_set1}:${match.team2_set1}`);
        if (match.team1_set2 !== null) {
          sets.push(`${match.team1_set2}:${match.team2_set2}`);
        }
        if (match.team1_set3 !== null && (match.team1_set3 > 0 || match.team2_set3 > 0)) {
          sets.push(`${match.team1_set3}:${match.team2_set3}`);
        }
        return { sets, isBestOfThree: true };
      }

      if (match.team1_score !== null) {
        return { score: `${match.team1_score}:${match.team2_score}`, isBestOfThree: false };
      }

      return null;
    };

    return (
      <div className="card">
        <h2 style={{ color: '#e8b73a', marginBottom: '20px' }}>⏰ Zeitplan - Alle Spiele</h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '100px 120px 100px 1fr 120px',
          gap: '1rem',
          padding: '1rem',
          background: '#0F172A',
          color: 'white',
          borderRadius: '8px',
          fontWeight: '600',
          fontSize: '0.9rem',
          marginBottom: '0.5rem'
        }}>
          <div>{t('admin.timeHeader')}</div>
          <div>{t('admin.matchHeader')}</div>
          <div>{t('common.courts')}</div>
          <div>{t('common.teams')}</div>
          <div>{t('admin.statusHeader')}</div>
        </div>

        {schedule.map((scheduleItem, index) => {
          const match = matches.find(m => m.id === scheduleItem.match_id);
          if (!match) return null;

          const team1 = teams.find(t => t.id === scheduleItem.team1_id);
          const team2 = teams.find(t => t.id === scheduleItem.team2_id);
          const result = getMatchResult(scheduleItem.match_id);

          return (
            <div
              id={`schedule-item-${match.id}`}
              key={scheduleItem.match_id}
              style={{
                display: 'grid',
                gridTemplateColumns: '100px 120px 100px 1fr 120px',
                gap: '1rem',
                padding: '1rem',
                background: match.status === 'beendet' ? 'rgba(34, 197, 94, 0.15)' :
                           match.status === 'läuft' ? 'rgba(232, 183, 58, 0.1)' : 'white',
                border: match.status === 'beendet' ? '1px solid rgba(34, 197, 94, 0.3)' :
                        match.status === 'läuft' ? '2px solid #e8b73a' : '1px solid rgba(232, 183, 58, 0.2)',
                borderRadius: '8px',
                alignItems: 'center',
                marginBottom: '0.5rem',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onClick={() => {
                // Bestimme den richtigen Tab basierend auf der Round und placement_category
                let targetTab = 'groups';
                const round = scheduleItem.round || match.round || '';
                const placementCategory = match.placement_category || '';

                // Check if it's a KO phase match (including Spiel um Platz 3)
                if (round.includes('Finale') || round.includes('Halbfinale') ||
                    round.includes('Viertelfinale') || round.includes('Achtelfinale') ||
                    round.includes('16tel') || round.includes('32tel') ||
                    round === 'Spiel um Platz 3' || placementCategory === 'main') {
                  targetTab = 'ko';
                }
                // Check if it's a placement match (but NOT Spiel um Platz 3)
                else if ((round.includes('Platz') || round.includes('Spiel um Platz')) &&
                         round !== 'Spiel um Platz 3') {
                  targetTab = 'placement';
                }
                // Otherwise it's a group phase match

                setActiveTab(targetTab);
                setTimeout(() => {
                  const matchElement = document.getElementById(`match-${match.id}`);
                  if (matchElement) {
                    matchElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    matchElement.style.border = '3px solid #e8b73a';
                    setTimeout(() => {
                      matchElement.style.border = '';
                    }, 2000);
                  }
                }, 100);
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                <div style={{ fontSize: '1rem', fontWeight: '600', color: match.start_time ? '#e8b73a' : '#0F172A' }}>
                  {formatTime(match.start_time || scheduleItem.start_time)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  -{formatTime(scheduleItem.end_time)}
                </div>
                {match.start_time && (
                  <div style={{ fontSize: '0.65rem', color: '#e8b73a' }}>✏️ manuell</div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#e8b73a' }}>
                  #{match.match_number}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {scheduleItem.round}
                </div>
              </div>

              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: match.court ? '#e8b73a' : '#0F172A', textAlign: 'center' }}>
                {match.court || scheduleItem.court || 'TBD'}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                <div style={{ flex: 1, fontWeight: '500', color: '#0F172A', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {team1 ? team1.players?.map(p => p.name).join(' / ') : 'TBD'}
                </div>
                <div style={{ fontWeight: 'bold', color: '#64748b', fontSize: '0.85rem', flexShrink: 0 }}>
                  {result ? (
                    result.isBestOfThree ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.75rem' }}>
                        {result.sets.map((set, idx) => (
                          <div key={idx}>{set}</div>
                        ))}
                      </div>
                    ) : (
                      result.score
                    )
                  ) : (
                    'vs'
                  )}
                </div>
                <div style={{ flex: 1, fontWeight: '500', color: '#0F172A', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {team2 ? team2.players?.map(p => p.name).join(' / ') : 'TBD'}
                </div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '0.3rem 0.8rem',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  background: match.status === 'beendet' ? 'rgba(40, 167, 69, 0.15)' :
                             match.status === 'läuft' ? 'rgba(232, 183, 58, 0.2)' : 'rgba(108, 117, 125, 0.1)',
                  color: match.status === 'beendet' ? '#28a745' :
                        match.status === 'läuft' ? '#d4910c' : '#6c757d'
                }}>
                  {match.status === 'beendet' ? t('admin.matchFinished') :
                   match.status === 'läuft' ? t('admin.matchRunning') :
                   t('admin.matchOpen')}
                </span>
              </div>
            </div>
          );
        })}

        <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(232, 183, 58, 0.08)', borderRadius: '8px', fontSize: '0.9rem', color: '#64748b' }}>
          {t('admin.tipClickMatch')}
        </div>
      </div>
    );
  };

  return (
    <div className="page-content" style={{ maxWidth: '1400px', position: 'relative' }}>
      <h1 style={{ marginBottom: '30px', fontSize: '2rem' }}>{t('nav.bracket')}</h1>

      {/* WebSocket Connection Status */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        borderRadius: '12px',
        backgroundColor: wsConnected ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
        border: `1px solid ${wsConnected ? '#4CAF50' : '#f44336'}`,
        fontSize: '12px',
        fontWeight: '600',
        color: wsConnected ? '#4CAF50' : '#f44336',
        zIndex: 100
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: wsConnected ? '#4CAF50' : '#f44336'
        }}></div>
        {wsConnected ? 'Live' : 'Offline'}
      </div>

      {message && (
        <div className="card" style={{
          padding: '15px',
          marginBottom: '20px',
          background: 'rgba(34, 197, 94, 0.15)',
          border: '1px solid #22c55e'
        }}>
          {message}
        </div>
      )}

      {error && (
        <div className="card" style={{
          padding: '15px',
          marginBottom: '20px',
          background: 'rgba(239, 68, 68, 0.2)',
          border: '1px solid #ef4444',
          color: '#f87171'
        }}>
          {error}
        </div>
      )}

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {/* Search Field */}
          {tournaments.length > 5 && (
            <div className="form-group" style={{ marginBottom: '0' }}>
              <label>{t('admin.searchTournament')}</label>
              <input
                type="text"
                placeholder={t('admin.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '10px',
                  fontSize: '14px',
                  borderRadius: '6px',
                  border: '1px solid var(--input-border)',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>
          )}

          {/* Tournament Dropdown */}
          <div className="form-group" style={{ marginBottom: '0' }}>
            <label>{t('admin.selectTournament')}</label>
            <select
              value={selectedTournamentId}
              onChange={(e) => setSelectedTournamentId(e.target.value)}
            >
              <option value="">{t('admin.selectDefault')}</option>

              {/* Active Tournaments */}
              {tournaments
                .filter(t =>
                  t.is_active === 1 &&
                  (!searchQuery ||
                    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (t.date && t.date.includes(searchQuery))
                  )
                )
                .length > 0 && (
                  <optgroup label={t('admin.activeTournaments')}>
                    {tournaments
                      .filter(t =>
                        t.is_active === 1 &&
                        (!searchQuery ||
                          t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (t.date && t.date.includes(searchQuery))
                        )
                      )
                      .map(tournament => (
                        <option key={tournament.id} value={tournament.id}>
                          {getTournamentLabel(tournament)}
                        </option>
                      ))}
                  </optgroup>
                )}

              {/* Completed Tournaments */}
              {tournaments
                .filter(t =>
                  t.is_active !== 1 &&
                  (!searchQuery ||
                    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (t.date && t.date.includes(searchQuery))
                  )
                )
                .length > 0 && (
                  <optgroup label={t('admin.inactiveTournaments')}>
                    {tournaments
                      .filter(t =>
                        t.is_active !== 1 &&
                        (!searchQuery ||
                          t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (t.date && t.date.includes(searchQuery))
                        )
                      )
                      .map(tournament => (
                        <option key={tournament.id} value={tournament.id}>
                          {getTournamentLabel(tournament)}
                        </option>
                      ))}
                  </optgroup>
                )}
            </select>
            {searchQuery && tournaments.filter(t =>
              t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (t.date && t.date.includes(searchQuery))
            ).length === 0 && (
              <div style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                Keine Turniere gefunden
              </div>
            )}
          </div>
        </div>
      </div>

      {tournament && (
        <div className="card" style={{
          marginBottom: '20px',
          background: tournament.is_active === 1 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(251, 146, 60, 0.15)',
          border: tournament.is_active === 1 ? '2px solid #22c55e' : '2px solid #fb923c'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <div>
              <h2 style={{ margin: '0 0 10px 0', fontSize: '1.5rem' }}>{tournament.name}</h2>
              {tournament.description && (
                <p style={{ margin: '0 0 10px 0', color: '#495057' }}>{tournament.description}</p>
              )}
              {tournament.date && (
                <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>
                  Datum: {formatDateWithLeadingZeros(tournament.date)}
                </p>
              )}
              {(tournament.created_by_first_name || tournament.created_by_last_name) && (
                <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#6c757d' }}>
                  Erstellt von: {[tournament.created_by_first_name, tournament.created_by_last_name].filter(Boolean).join(' ')}
                </p>
              )}
            </div>

            <button
              onClick={handleToggleActive}
              style={{
                padding: '12px 24px',
                background: tournament.is_active === 1 ? '#ef4444' : '#22c55e'
              }}
            >
              {tournament.is_active === 1 ? t('common.deactivate') : t('common.activate')}
            </button>
          </div>

          <div style={{ marginTop: '15px', fontSize: '14px', fontWeight: 'bold' }}>
            Status: {tournament.is_active === 1 ? (
              <span style={{ color: '#22c55e' }}>✓ AKTIV (wird in Display View angezeigt)</span>
            ) : (
              <span style={{ color: '#fb923c' }}>○ INAKTIV</span>
            )}
          </div>
        </div>
      )}

      {loading && <p>{t('admin.loadingTournament')}</p>}

      {!loading && tournament && tournament.tournament_type === 'groups' && (
        <>
          {/* Tab Navigation for Group Tournaments */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '20px',
            borderBottom: '2px solid #e8b73a',
            paddingBottom: '10px'
          }}>
            <button
              onClick={() => setActiveTab('groups')}
              style={{
                padding: '10px 20px',
                background: activeTab === 'groups' ? '#e8b73a' : 'transparent',
                color: activeTab === 'groups' ? '#0F172A' : '#e8b73a',
                border: 'none',
                borderRadius: '6px 6px 0 0',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              📊 Gruppenphase
            </button>
            <button
              onClick={() => setActiveTab('ko')}
              style={{
                padding: '10px 20px',
                background: activeTab === 'ko' ? '#e8b73a' : 'transparent',
                color: activeTab === 'ko' ? '#0F172A' : '#e8b73a',
                border: 'none',
                borderRadius: '6px 6px 0 0',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              🏆 K.O.-Runde
            </button>
            <button
              onClick={() => setActiveTab('rankings')}
              style={{
                padding: '10px 20px',
                background: activeTab === 'rankings' ? '#e8b73a' : 'transparent',
                color: activeTab === 'rankings' ? '#0F172A' : '#e8b73a',
                border: 'none',
                borderRadius: '6px 6px 0 0',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              📋 Rangliste
            </button>
            {tournament.start_time && (
              <button
                onClick={() => setActiveTab('schedule')}
                style={{
                  padding: '10px 20px',
                  background: activeTab === 'schedule' ? '#e8b73a' : 'transparent',
                  color: activeTab === 'schedule' ? '#0F172A' : '#e8b73a',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                ⏰ Zeitplan
              </button>
            )}
          </div>

          {/* Tab Content */}
          {activeTab === 'groups' && renderGroupsOverview()}
          {activeTab === 'ko' && renderKOPHase()}
          {activeTab === 'schedule' && renderScheduleTab()}
          {activeTab === 'rankings' && (
            <div className="card">
              <h2 style={{ color: '#e8b73a', marginBottom: '20px' }}>📋 Finale Rangliste</h2>

              {completionRankings && completionRankings.main && completionRankings.main.length > 0 ? (
                <div>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    marginTop: '20px'
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: 'rgba(232, 183, 58, 0.2)' }}>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e8b73a' }}>Platz</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e8b73a' }}>Team</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completionRankings.main.map((ranking, index) => (
                        <tr key={index} style={{
                          backgroundColor: ranking.place <= 3 ? (
                            ranking.place === 1 ? 'rgba(255, 215, 0, 0.2)' :
                            ranking.place === 2 ? 'rgba(192, 192, 192, 0.2)' :
                            'rgba(205, 127, 50, 0.2)'
                          ) : 'transparent'
                        }}>
                          <td style={{
                            padding: '12px',
                            borderBottom: '1px solid rgba(206, 212, 218, 0.3)',
                            fontWeight: ranking.place <= 3 ? 'bold' : 'normal',
                            fontSize: ranking.place <= 3 ? '1.1rem' : '1rem'
                          }}>
                            {ranking.place === 1 ? '🥇' : ranking.place === 2 ? '🥈' : ranking.place === 3 ? '🥉' : ''} {ranking.place}
                          </td>
                          <td style={{
                            padding: '12px',
                            borderBottom: '1px solid rgba(206, 212, 218, 0.3)',
                            fontWeight: ranking.place <= 3 ? 'bold' : 'normal'
                          }}>
                            {ranking.team}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div>
                  <p style={{ marginBottom: '20px', color: '#6c757d' }}>
                    Das Turnier ist noch nicht abgeschlossen. Bitte alle Spiele beenden und Turnier abschließen.
                  </p>
                  {!tournamentCompleted && (
                    <button
                      onClick={handleCompleteTournament}
                      style={{
                        padding: '12px 24px',
                        background: '#e8b73a',
                        color: '#0F172A',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#d4a02e'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#e8b73a'}
                    >
                      🏆 Turnier abschließen & Rangliste generieren
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!loading && tournament && tournament.tournament_type !== 'groups' && (
        <>
          {/* Keep original rendering for non-group tournaments */}
          {tournament.tournament_type === 'groups' && renderGroupsOverview()}
          {tournament.tournament_type === 'groups' && renderKOPHase()}
          {tournament.tournament_type === 'ko' && renderKOMatches()}
          {/* KO mit Platzierungen */}
          {tournament.tournament_type === 'ko_placement' && renderKOPlacementView()}
          {/* Mexicano tournament view */}
          {tournament.tournament_type === 'mexicano' && renderMexicanoView()}
          {/* Americano tournament view */}
          {tournament.tournament_type === 'americano' && renderMexicanoView()}
          {/* Landesliga tournament view */}
          {tournament.tournament_type === 'landesliga' && renderLandesligaMatches()}
        </>
      )}

      {/* Modal: Ergebnis direkt im Bracket eintragen */}
      {bracketModal && (() => {
        const m = bracketModal;
        const t1 = teams.find(t => t.id === m.team1_id);
        const t2 = teams.find(t => t.id === m.team2_id);
        const isBo3 = tournament?.best_of_three === 1;
        const label1 = t1?.players?.map(p => p.name.split(' ')[0]).join('/') || t1?.name || 'Team 1';
        const label2 = t2?.players?.map(p => p.name.split(' ')[0]).join('/') || t2?.name || 'Team 2';
        const name1 = t1?.players?.map(p => p.name).join(' / ') || t1?.name || '—';
        const name2 = t2?.players?.map(p => p.name).join(' / ') || t2?.name || '—';

        const inp = (id, defaultVal, isTiebreak) => (
          <input
            key={id}
            id={`bm-${id}`}
            type="number"
            min="0"
            max={isTiebreak ? 20 : 7}
            defaultValue={defaultVal !== null && defaultVal !== undefined ? defaultVal : ''}
            placeholder="0"
            autoComplete="off"
            style={{
              width: '100%', padding: '10px', fontSize: '20px', fontWeight: 'bold',
              textAlign: 'center', borderRadius: '6px',
              border: `2px solid ${isTiebreak ? '#e8b73a' : 'rgba(206,212,218,0.6)'}`,
              background: isTiebreak ? 'rgba(254,243,199,0.3)' : 'white', color: '#0F172A',
            }}
          />
        );

        const saveCourtTime = async () => {
          const { court, start_time, note } = bracketModalMeta;
          const noteVal = note || '';
          if (court !== (m.court || '') || start_time !== (m.start_time || '') || noteVal !== (m.note || '')) {
            try {
              await axios.put(`/api/matches/${m.id}/details`, { court, start_time, note: noteVal });
              loadTournamentData(selectedTournamentId);
            } catch (err) {
              console.error('Fehler beim Speichern von Platz/Zeit:', err);
            }
          }
        };

        const handleSave = async () => {
          // Court/Zeit immer mitspeichern
          await saveCourtTime();

          if (isBo3) {
            const v = (id) => document.getElementById(`bm-${id}`)?.value;
            const t1s1 = v('t1s1'), t2s1 = v('t2s1'), t1s2 = v('t1s2'), t2s2 = v('t2s2');
            const t1s3 = v('t1s3'), t2s3 = v('t2s3');
            if (!t1s1 || !t2s1 || !t1s2 || !t2s2) {
              setError(t('admin.errorEnterSets'));
              setTimeout(() => setError(''), 3000);
              return;
            }
            handleUpdateMatch(m.id, {
              team1_set1: parseInt(t1s1) || 0, team2_set1: parseInt(t2s1) || 0,
              team1_set2: parseInt(t1s2) || 0, team2_set2: parseInt(t2s2) || 0,
              team1_set3: t1s3 !== '' && t1s3 != null ? parseInt(t1s3) : null,
              team2_set3: t2s3 !== '' && t2s3 != null ? parseInt(t2s3) : null,
            });
          } else {
            const s1 = document.getElementById('bm-s1')?.value;
            const s2 = document.getElementById('bm-s2')?.value;
            if (!s1 || !s2) {
              setError(t('admin.errorEnterBothScores'));
              setTimeout(() => setError(''), 3000);
              return;
            }
            handleUpdateMatch(m.id, { team1_score: parseInt(s1), team2_score: parseInt(s2) });
          }
          setBracketModal(null);
        };

        const handleSaveMetaOnly = async () => {
          await saveCourtTime();
          setMessage('Platz / Zeit gespeichert');
          setTimeout(() => setMessage(''), 3000);
          setBracketModal(null);
        };

        return (
          <div
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}
            onClick={(e) => { if (e.target === e.currentTarget) setBracketModal(null); }}
          >
            <div className="card" style={{ padding: 0, minWidth: 360, maxWidth: 480, width: '90vw', overflow: 'hidden', borderRadius: 12 }}>
              {/* Header */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(206,212,218,0.4)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{m.round}</div>
                  <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.4 }}>{name1}</div>
                  <div style={{ fontSize: 12, color: '#6c757d', margin: '3px 0' }}>vs</div>
                  <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.4 }}>{name2}</div>
                </div>
                <button onClick={() => setBracketModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6c757d', lineHeight: 1, paddingLeft: 12 }}>✕</button>
              </div>

              {/* Court / Zeit */}
              <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(206,212,218,0.3)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, color: '#6c757d', fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('admin.courtLabel')}</label>
                  <input
                    type="text"
                    value={bracketModalMeta.court}
                    onChange={(e) => setBracketModalMeta(prev => ({ ...prev, court: e.target.value }))}
                    placeholder={t('admin.courtPlaceholder')}
                    style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1.5px solid rgba(206,212,218,0.6)', fontSize: 13, color: '#0F172A', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#6c757d', fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('admin.timeLabel')}</label>
                  <input
                    type="time"
                    value={bracketModalMeta.start_time}
                    onChange={(e) => setBracketModalMeta(prev => ({ ...prev, start_time: e.target.value }))}
                    style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1.5px solid rgba(206,212,218,0.6)', fontSize: 13, color: '#0F172A', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Notiz */}
              <div style={{ padding: '10px 20px', borderBottom: '1px solid rgba(206,212,218,0.3)' }}>
                <label style={{ fontSize: 11, color: '#6c757d', fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('admin.noteLabel')}</label>
                <textarea
                  value={bracketModalMeta.note}
                  onChange={(e) => setBracketModalMeta(prev => ({ ...prev, note: e.target.value }))}
                  placeholder={t('common.notePlaceholder')}
                  rows={2}
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1.5px solid rgba(206,212,218,0.6)', fontSize: 13, color: '#0F172A', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              {/* Eingabe – nur wenn beide Teams feststehen */}
              {m.team1_id && m.team2_id && <div style={{ padding: '20px' }}>
                {isBo3 ? (
                  [[t('admin.set1'), 't1s1', 't2s1', m.team1_set1, m.team2_set1, false],
                   [t('admin.set2'), 't1s2', 't2s2', m.team1_set2, m.team2_set2, false],
                   [t('admin.tiebreak'), 't1s3', 't2s3', m.team1_set3, m.team2_set3, true]
                  ].map(([rowLabel, id1, id2, def1, def2, isTb]) => (
                    <div key={rowLabel} style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: isTb ? '#e8b73a' : '#6c757d', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {rowLabel}{isTb && <span style={{ fontWeight: 400 }}> – {t('admin.onlyIfTied')}</span>}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 20px 1fr', gap: 8, alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 3, textAlign: 'center' }}>{label1}</div>
                          {inp(id1, def1, isTb)}
                        </div>
                        <div style={{ textAlign: 'center', color: '#94a3b8', fontWeight: 700, fontSize: 16 }}>:</div>
                        <div>
                          <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 3, textAlign: 'center' }}>{label2}</div>
                          {inp(id2, def2, isTb)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 20px 1fr', gap: 8, alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6, textAlign: 'center', fontWeight: 600 }}>{label1}</div>
                      {inp('s1', m.team1_score, false)}
                    </div>
                    <div style={{ textAlign: 'center', color: '#94a3b8', fontWeight: 700, fontSize: 16 }}>:</div>
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6, textAlign: 'center', fontWeight: 600 }}>{label2}</div>
                      {inp('s2', m.team2_score, false)}
                    </div>
                  </div>
                )}
              </div>}

              {/* Footer */}
              <div style={{ padding: '12px 20px 16px', display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid rgba(206,212,218,0.4)', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setBracketModal(null)}
                  style={{ padding: '8px 18px', borderRadius: 6, border: '1px solid rgba(206,212,218,0.7)', background: 'none', cursor: 'pointer', color: '#6c757d', fontSize: 14 }}
                >
                  {t('common.cancel')}
                </button>
                {m.team1_id && m.team2_id ? (
                  <>
                    <button
                      onClick={handleSaveMetaOnly}
                      style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid rgba(232,183,58,0.6)', background: 'rgba(232,183,58,0.1)', cursor: 'pointer', color: '#92611a', fontSize: 13 }}
                    >
                      {t('admin.saveCourtOnly')}
                    </button>
                    <button
                      onClick={handleSave}
                      style={{ padding: '8px 22px', borderRadius: 6, border: 'none', background: '#22c55e', cursor: 'pointer', fontWeight: 700, color: '#0F172A', fontSize: 14 }}
                    >
                      {t('admin.saveResult')}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleSaveMetaOnly}
                    style={{ padding: '8px 22px', borderRadius: 6, border: 'none', background: '#e8b73a', cursor: 'pointer', fontWeight: 700, color: '#0F172A', fontSize: 14 }}
                  >
                    {t('admin.saveCourt')}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal: Platz / Uhrzeit bearbeiten */}
      {editingMatch && (() => {
        const calcEndTime = (startStr) => {
          if (!startStr || !tournament?.match_duration) return null;
          const [h, m] = startStr.split(':').map(Number);
          const total = h * 60 + m + tournament.match_duration;
          return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
        };
        const endTime = calcEndTime(editingMatch.start_time);
        return (
          <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
          }}>
            <div className="card" style={{ padding: '24px', minWidth: '320px', backgroundColor: 'var(--card-bg, white)' }}>
              <h3 style={{ marginBottom: '20px', fontSize: '1.1rem' }}>{t('admin.editModalTitle')}</h3>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', color: '#6c757d', marginBottom: '6px' }}>
                  {t('admin.courtLabel')}
                </label>
                <input
                  type="text"
                  value={editingMatch.court}
                  onChange={(e) => setEditingMatch({ ...editingMatch, court: e.target.value })}
                  placeholder="z.B. Court 1"
                  style={{
                    width: '100%', padding: '8px 12px', fontSize: '1rem',
                    border: '1px solid rgba(206,212,218,0.8)', borderRadius: '6px',
                    background: 'var(--input-bg, white)', color: 'var(--text-primary, #0F172A)'
                  }}
                />
              </div>
              <div style={{ marginBottom: endTime ? '8px' : '20px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', color: '#6c757d', marginBottom: '6px' }}>
                  {t('admin.timeLabel')}
                </label>
                <input
                  type="time"
                  value={editingMatch.start_time}
                  onChange={(e) => setEditingMatch({ ...editingMatch, start_time: e.target.value })}
                  style={{
                    width: '100%', padding: '8px 12px', fontSize: '1rem',
                    border: '1px solid rgba(206,212,218,0.8)', borderRadius: '6px',
                    background: 'var(--input-bg, white)', color: 'var(--text-primary, #0F172A)'
                  }}
                />
              </div>
              {endTime && (
                <div style={{ marginBottom: '20px', fontSize: '0.85rem', color: '#6c757d', padding: '6px 12px', background: 'rgba(232,183,58,0.08)', borderRadius: '6px' }}>
                  Endzeit: <strong style={{ color: '#e8b73a' }}>{endTime}</strong>
                  {tournament?.match_duration && <span> ({tournament.match_duration} Min. Spielzeit)</span>}
                </div>
              )}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', color: '#6c757d', marginBottom: '6px' }}>
                  {t('admin.noteLabel')}
                </label>
                <textarea
                  value={editingMatch.note}
                  onChange={(e) => setEditingMatch({ ...editingMatch, note: e.target.value })}
                  placeholder={t('common.notePlaceholder')}
                  rows={2}
                  style={{
                    width: '100%', padding: '8px 12px', fontSize: '0.9rem',
                    border: '1px solid rgba(206,212,218,0.8)', borderRadius: '6px',
                    background: 'var(--input-bg, white)', color: 'var(--text-primary, #0F172A)',
                    resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setEditingMatch(null)}
                  style={{
                    padding: '8px 16px', borderRadius: '6px', border: '1px solid rgba(206,212,218,0.8)',
                    background: 'none', cursor: 'pointer', color: '#6c757d'
                  }}
                >{t('common.cancel')}</button>
                <button
                  onClick={handleSaveMatchDetails}
                  style={{
                    padding: '8px 16px', borderRadius: '6px', border: 'none',
                    background: '#e8b73a', cursor: 'pointer', fontWeight: '600', color: '#0F172A'
                  }}
                >{t('common.save')}</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default AdminTournamentBracket;
